import isEmpty from 'is-empty';

import { Logger } from '@gitbook/util-debug';
import { List } from '@gitbook/util-immutable';
import { assert, assertIsDefined, assertNever, filterOutNullable } from '@gitbook/util-ts';

import { isType, MODEL_TYPES } from '../constants';
// Prevents a circular dependency
import { applyOperation } from '../operations/apply';
import { invertOperation } from '../operations/invert';
import {
    generateKey,
    getCharOffsetBackward,
    getCharOffsetForward,
    getWordOffsetBackward,
    getWordOffsetForward,
    hasBlockRangeProperties,
    isBlockRangeSelection,
    isSwitchingSelection,
    isTextRangeSelection,
    pick,
} from '../utils';
import { Block, type BlockCreateProps } from './block';
import { BlockRange, type BlockRangeCreateProps } from './block-range';
import type { Document } from './document';
import { Fragment } from './fragment';
import { Inline, type InlineCreateProps } from './inline';
import type { Leaf } from './leaf';
import { isPathBefore, isPathEqual, type Path } from './lookup-index';
import { Mark, type MarkCreateAttrs, type MarkCreateSetProps } from './mark';
import type { AncestorNode, ChildNode, KeyedNode } from './node';
import { createNodeProperties } from './node-properties';
import { Operation } from './operation';
import type { Schema } from './schema';
import { Text } from './text';
import { TextRange, type TextRangeCreateProps } from './text-range';
import type { SelectionRange, Value } from './value';

const logger = Logger('slate:change');

/**
 * A text range that is known to be set (an expanded/collapsed selection with
 * resolved endpoints), so its anchor/focus/start/end keys are non-null.
 */
type SetTextRange = TextRange & {
    anchorKey: string;
    focusKey: string;
    startKey: string;
    endKey: string;
};

interface ApplyOperationOptions {
    /**
     * If true, the operation will be saved in the history.
     * @default true
     */
    save?: boolean;

    /**
     * If true, skip merging this operation with the previous one in history.
     */
    skip?: boolean;

    /**
     * If true, merge this operation with the previous one in history.
     */
    merge?: boolean;
}

export interface ChangeFlags {
    normalize?: boolean;
    merge?: boolean;
    save?: boolean;
    target?: null | number;
}

interface MarkOptions {
    property?: string;
}

/**
 * Changes inside a normalization are under stricter rules than changes made to a document:
 *
 * - normalization can only change the current node or nodes ahead of it in the subtree traversal, but never nodes before it. This is to prevent the normalization from having to rescan the same subtree multiple times.
 *
 * A normalization scope tracks the current position of normalization in the document and can be used to detect if something has broken these rules.
 */
interface NormalizationScope {
    /**
     * Root path of the parent subtree currently being normalized.
     */
    rootPath: Path;
    /**
     * Path of the child we most recently normalized. Writes before this path
     * are considered backward rewrites for the active subtree pass.
     */
    resumePath: Path;
    /**
     * Set when a normalization writes to a path behind it.
     */
    hasUnsafeOperations: boolean;
    /**
     * Path of the node whose normalize callback is currently running.
     *
     * Writes inside this subtree are local repairs to the active node and are
     * allowed even when they sort before the surrounding resume cursor.
     */
    currentNodePath: Path | null;
}

// Get the type of the tail of an array
type Tail<T extends any[]> = ((...t: T) => void) extends (h: any, ...r: infer R) => void
    ? R
    : never;

/*
 * Mutable interface to make changes to a value.
 */
export class Change {
    public static logger = logger;

    /*
     * Check if `any` is a `Change`.
     */
    public static isChange = isType.bind(null, 'CHANGE');

    /** Initial immutable value when the change is created */
    public readonly initialValue: Value;

    // Mutable properties

    /** Mutable current value for the editor */
    public value: Value;

    /** All operations generated by the changes ([operation, valueAfter, valueBefore]) */
    public operations: List<[Operation, Value, Value]>;

    /** All operations to push in the history. */
    private savedOperations: Operation[];

    /** Configuration flags for the changes */
    public flags: ChangeFlags;

    /** Boolean to store if the change has been finalized. */
    private finalized = false;

    /** Context map of normalizations occuring in this change */
    private normalizations: {
        [nodeKey: string]: {
            /** If true, the normalization for this node will run once all of its children have been processed */
            pending: boolean;
        };
    } = {};

    /** Active normalization scopes in the current change. */
    private normalizationScopes: NormalizationScope[] = [];

    /*
     * Create a new `Change` with `attrs`.
     */
    constructor(
        attrs: {
            value: Value;
        } & ChangeFlags
    ) {
        const { value } = attrs;
        this.initialValue = value;
        this.value = value;
        this.operations = List.create();
        this.savedOperations = [];

        this.flags = {
            normalize: 'normalize' in attrs ? attrs.normalize : true,
            merge: attrs.merge,
            save: attrs.merge,
        };
    }

    /*
     * Object.
     */
    get object(): 'change' {
        return 'change';
    }

    /**
     * Return true if the change has been applying operations to the document.
     */
    public get isDirty(): boolean {
        return this.operations.size > 0;
    }

    /*
     * Apply an `operation` to the current value, saving the operation to the
     * history if needed.
     */
    public applyOperation(operation: Operation, options: ApplyOperationOptions = {}): Change {
        if (this.finalized) {
            throw new Error('Change has already been finalized.');
        }

        const { operations, flags } = this;
        let { value } = this;

        const beforeValue = value;

        // Default options to the change-level flags, this allows for setting
        // specific options for all of the operations of a given change.
        options = { ...flags, ...options };
        const { save = true } = options;

        // if we're in a normalization pass, track this operation to see if it passes stricter normalization rules
        this.trackNormalizationOperation(operation);

        // Apply the operation to the value.
        value = applyOperation(value, operation);

        // If needed, save the operation to the history.
        if (save && operation.type !== 'set_selection') {
            this.savedOperations.unshift(operation);
        }

        // Update the mutable change object.
        this.operations = operations.push([operation, value, beforeValue]);
        this.value = value;

        return this;
    }

    /**
     * Apply a series of `operations` to the current value.
     */
    public applyOperations(operations: Operation[], options?: ApplyOperationOptions): Change {
        operations.forEach((op) => this.applyOperation(op, options));
        return this;
    }

    /**
     * Finalize a change by normalizing it and saving it in the history.
     */
    public finalize(): Change {
        // All changes should result in a fully normalized document
        // But we don't run the entire normalization process on each change
        // to improve performances.
        // During development, we check that the change will not result in a broken document.
        if (process.env.NODE_ENV !== 'production') {
            const previousOperations = this.operations;
            const previousDocument = this.value.document;
            this.normalize();
            if (this.operations.size !== previousOperations.size) {
                logger.error('Document is broken after change', previousDocument);
                logger.error(
                    'Operations before normalization',
                    previousOperations.toArray().map(([op]) => op.toObject())
                );
                logger.error(
                    'Operations after normalization',
                    this.operations.toArray().map(([op]) => op.toObject())
                );
                throw new Error(
                    'Changes should not result in a broken document. Make sure that you are correctly normalizing the impacted nodes.'
                );
            }
        }

        const selection = this.value.selection.normalizeSelection(this.value.document);
        if (selection !== this.value.selection) {
            this.select(selection);
        }

        this.saveHistory();

        this.finalized = true;

        return this;
    }

    /**
     * Save the current changes in the history to be undone using `change.undo()` on the value.
     */
    public saveHistory(options: { merge: boolean } = { merge: true }): Change {
        if (this.savedOperations.length === 0) {
            return this;
        }

        const undoOperations = this.savedOperations.map(invertOperation);
        this.value = this.value.merge({
            history: this.value.history.save({
                operations: undoOperations,
                selectionBefore: this.initialValue.selection as TextRange,
                merge: options.merge,
            }),
        });
        this.savedOperations = [];

        return this;
    }

    /*
     * Call a change `fn` with arguments.
     */
    public call<U extends any[]>(fn: (c: Change, ...args: U) => void, ...args: U): Change {
        fn(this, ...args);
        return this;
    }

    /*
     * Applies a series of change mutations and defers normalization until the end.
     */
    public withoutNormalization(customChange: (c: Change) => void): Change {
        const original = this.flags.normalize;
        this.setOperationFlag('normalize', false);

        try {
            customChange(this);
        } finally {
            // restore the flag to whatever it was
            this.setOperationFlag('normalize', original);
        }
        return this;
    }

    /*
     * Set an operation flag by `key` to `value`.
     */
    public setOperationFlag(key: string, value: any): Change {
        this.flags[key] = value;
        return this;
    }

    /*
     * Get the `value` of the specified flag by its `key`. Optionally accepts an `options`
     * object with override flags.
     */
    public getFlag<T extends keyof ChangeFlags>(
        key: T,
        options: ChangeFlags = {}
    ): ChangeFlags[T] | undefined {
        return options[key] !== undefined ? options[key] : this.flags[key];
    }

    /*
     * Unset an operation flag by `key`.
     */
    public unsetOperationFlag(key: string): Change {
        delete this.flags[key];
        return this;
    }

    /**
     * Add a value decoration.
     * The decoration is not stored in the document and can be used to render temporary marks.
     */
    public setValueDecoration(range: TextRange): Change {
        this.value = this.value.merge({
            decorations: this.value.decorations.add(range),
        });

        return this;
    }

    /**
     * Remove a value decoration, previously added with `setValueDecoration`.
     */
    public removeValueDecoration(range: TextRange): Change {
        this.value = this.value.merge({
            decorations: this.value.decorations.delete(range),
        });

        return this;
    }

    /**
     * Undo the previous operations in the history (from `undos`).
     */
    public undo(): Change {
        const undo = this.value.history.undo();
        if (!undo) {
            return this;
        }

        // Replay the already-inverted operations.
        undo.operations.forEach((operation) => {
            this.applyOperation(operation, { save: false });
        });

        // Update the history.
        this.value = this.value.set('history', undo.history);

        if (undo.selection) {
            this.select(
                undo.selection.merge({
                    // Prevent the editor focus from changing during undoing.
                    isFocused: this.value.isFocused,
                })
            );
        }

        return this;
    }

    /**
     * Redo to the next operations in the history (from `redos`).
     */
    public redo(): Change {
        const redo = this.value.history.redo();
        if (!redo) {
            return this;
        }

        if (redo.selection) {
            this.select(
                redo.selection.merge({
                    // Prevent the editor focus from changing during redoing.
                    isFocused: this.value.isFocused,
                })
            );
        }

        redo.operations.forEach((operation) => {
            this.applyOperation(operation, { save: false });
        });

        // Update the history.
        this.value = this.value.set('history', redo.history);

        return this;
    }

    public selectBlock(range: BlockRange, options: { snapshot?: boolean } = {}): Change {
        const { snapshot = false } = options;

        const properties = BlockRange.createProperties(range);

        // Apply the operation.
        this.applyOperation(
            Operation.create({
                type: 'set_selection',
                properties,
                selection: range,
            }),
            snapshot ? { skip: false, merge: false } : {}
        );

        return this;
    }

    public selectText(range: TextRange, options: { snapshot?: boolean } = {}): Change {
        const { snapshot = false } = options;
        const properties = TextRange.createProperties(range);

        // Apply the operation.
        this.applyOperation(
            Operation.create({
                type: 'set_selection',
                properties,
                selection: range,
            }),
            snapshot ? { skip: false, merge: false } : {}
        );

        return this;
    }

    /**
     * Switch the selection between a text range and a block range.
     */
    public switchSelection() {
        const { value } = this;
        const { document, selection } = value;
        if (isTextRangeSelection(selection)) {
            return this.selectBlock(BlockRange.fromTextRange(selection, document));
        }
        return this.selectText(TextRange.fromBlockRange(selection, document).collapseToStart());
    }

    /**
     * Set `properties` on the selection.
     */
    public select(
        input: TextRangeCreateProps | BlockRangeCreateProps,
        options: { snapshot?: boolean } = {}
    ): Change {
        const { snapshot = false } = options;
        const { value } = this;
        const { document, selection } = value;

        // determine whether the new selection is a block or text range
        const isBlockRangeInput = hasBlockRangeProperties(input);
        let properties = isBlockRangeInput
            ? BlockRange.createProperties(input)
            : TextRange.createProperties(input);

        // determine whether we are switching between block and text ranges.
        // If we are switching we can't merge the selection but will have to
        // create a new one
        if (isSwitchingSelection(selection, properties)) {
            if (isBlockRangeInput) {
                return this.selectBlock(BlockRange.create(properties as BlockRangeCreateProps));
            }
            return this.selectText(
                TextRange.create(properties as TextRangeCreateProps).collapseToStart()
            );
        }

        const props: TextRangeCreateProps | BlockRangeCreateProps = {};
        const next = selection.merge(properties).normalize(document);
        const sel = selection.toObject();
        properties = pick(next, Object.keys(properties));

        // Remove any properties that are already equal to the current selection. And
        // create a dictionary of the previous values for all of the properties that
        // are being changed, for the inverse operation.
        for (const k in properties) {
            if (snapshot === false && properties[k] === sel[k]) {
                continue;
            }
            props[k] = properties[k];
        }

        // If the selection moves, clear any marks, unless the new selection
        // properties change the marks in some way.
        const moved = ['anchorKey', 'anchorOffset', 'focusKey', 'focusOffset'].some((p) =>
            Object.hasOwn(props, p)
        );

        if (
            (sel as TextRange).marks &&
            (properties as TextRange).marks === (sel as TextRange).marks &&
            moved
        ) {
            (props as { marks: MarkCreateAttrs[] | null }).marks = null;
        }

        if (!snapshot && selection.equals(next)) {
            logger.warning(
                `Skipping change.select because it's called with the same selection. It may indicate an issue with event handlers.`
            );
            return this;
        }

        // If there are no new properties to set, abort.
        if (isEmpty(props)) {
            return this;
        }

        // Apply the operation.
        this.applyOperation(
            Operation.create({
                type: 'set_selection',
                properties: props,
                selection: next,
            }),
            snapshot ? { skip: false, merge: false } : {}
        );

        return this;
    }

    /**
     * Select the whole current fragment or document.
     */
    public selectAll(): Change {
        const { value } = this;
        const { document } = value;
        const selection = value.selection as TextRange;

        // Select either everything in the document, or in the Fragment
        // (if currently inside a Fragment)
        const selectionTarget = document.getFragmentParent(selection.anchorKey!);
        if (!selectionTarget) {
            return this;
        }

        const next = selection.moveToRangeOf(selectionTarget);
        this.select(next);

        return this;
    }

    /**
     * Select the whole document.
     */
    public selectAllDocument(): Change {
        const { value } = this;
        const { document } = value;
        const selection = value.selection as TextRange;

        const next = selection.moveToRangeOf(document);
        this.select(next);

        return this;
    }

    public selectAllBlockRange(): Change {
        const { value } = this;
        const { document, selection } = value;

        if (!isBlockRangeSelection(selection)) {
            return this;
        }

        const next = selection.moveAnchorToStartOf(document).moveFocusToEndOf(document);
        this.select(next);

        return this;
    }
    /**
     * Snapshot the current selection.
     */
    public snapshotSelection(): Change {
        const { value } = this;
        const { selection } = value;
        this.select(selection, { snapshot: true });

        return this;
    }

    public resizeBlockRangeBackward(): Change {
        const { document, selection } = this.value;
        if (!isBlockRangeSelection(selection)) {
            return this;
        }
        return this.select(selection.moveFocusBackward(document));
    }

    public resizeBlockRangeForward(): Change {
        const { document, selection } = this.value;
        if (!isBlockRangeSelection(selection)) {
            return this;
        }
        return this.select(selection.moveFocusForward(document));
    }

    /*
     * Collapse and move the block range to the block's next descendant block (if it has one)
     */
    public moveBlockRangeToDescendant(): Change {
        const { document, selection } = this.value;
        if (!isBlockRangeSelection(selection)) {
            return this;
        }
        return this.select(selection.moveSelectionToDescendant(document));
    }

    /*
     * Collapse and move the bock range to the block's next ancestor block (if it has one)
     */
    public moveBlockRangeToAncestor(): Change {
        const { document, selection } = this.value;
        if (!isBlockRangeSelection(selection)) {
            return this;
        }
        return this.select(selection.moveSelectionToAncestor(document));
    }

    /*
     * Collapse and move a block range backward.
     */
    public moveBlockRangeBackward(): Change {
        const { value } = this;
        const { document, selection } = value;

        if (!isBlockRangeSelection(selection)) {
            return this;
        }

        const next = selection.moveSelectionBackward(document);
        return this.select(next);
    }

    /*
     * Collapse and move a block range forward.
     */
    public moveBlockRangeForward(): Change {
        const { value } = this;
        const { document, selection } = value;

        if (!isBlockRangeSelection(selection)) {
            return this;
        }

        const next = selection.moveSelectionForward(document);
        return this.select(next);
    }

    /**
     * Move the anchor point backward, accounting for being at the start of a block.
     */
    public moveAnchorCharBackward(): Change {
        const { value } = this;
        const { document } = value;
        const selection = value.selection as TextRange;
        const anchorText = value.anchorText!;
        const anchorBlock = value.anchorBlock!;
        const { anchorOffset } = selection;
        const previousText = document.getPreviousText(anchorText.key)!;
        const isInVoid = document.hasVoidParent(anchorText.key);
        const isPreviousInVoid = previousText && document.hasVoidParent(previousText.key);

        if (!isInVoid && anchorOffset > 0) {
            this.moveAnchor(-1);
            return this;
        }

        if (!previousText) {
            return this;
        }

        this.moveAnchorToEndOf(previousText);

        if (!isInVoid && !isPreviousInVoid && anchorBlock.hasNode(previousText.key)) {
            this.moveAnchor(-1);
        }

        return this;
    }

    /**
     * Move the anchor point forward, accounting for being at the end of a block.
     */
    public moveAnchorCharForward(): Change {
        const { value } = this;
        const { document } = value;
        const selection = value.selection as TextRange;
        const anchorText = value.anchorText!;
        const anchorBlock = value.anchorBlock!;
        const { anchorOffset } = selection;
        const nextText = document.getNextText(anchorText.key)!;
        const isInVoid = document.hasVoidParent(anchorText.key);
        const isNextInVoid = nextText && document.hasVoidParent(nextText.key);

        if (!isInVoid && anchorOffset < anchorText.text.length) {
            this.moveAnchor(1);
            return this;
        }

        if (!nextText) {
            return this;
        }

        this.moveAnchorToStartOf(nextText);

        if (!isInVoid && !isNextInVoid && anchorBlock.hasNode(nextText.key)) {
            this.moveAnchor(1);
        }

        return this;
    }

    /**
     * Move the focus point backward, accounting for being at the start of a block.
     */
    public moveFocusCharBackward(): Change {
        const { value } = this;
        const { document } = value;
        const selection = value.selection as TextRange;
        const focusText = value.focusText!;
        const focusBlock = value.focusBlock!;
        const { focusOffset } = selection;
        const previousText = document.getPreviousText(focusText.key)!;
        const isInVoid = document.hasVoidParent(focusText.key);
        const isPreviousInVoid = previousText && document.hasVoidParent(previousText.key);

        if (!isInVoid && focusOffset > 0) {
            this.moveFocus(-1);
            return this;
        }

        if (!previousText) {
            return this;
        }

        this.moveFocusToEndOf(previousText);

        if (!isInVoid && !isPreviousInVoid && focusBlock.hasNode(previousText.key)) {
            this.moveFocus(-1);
        }

        return this;
    }

    /**
     * Move the focus point forward, accounting for being at the end of a block.
     */
    public moveFocusCharForward(): Change {
        const { value } = this;
        const { document } = value;
        const selection = value.selection as TextRange;
        const focusText = value.focusText!;
        const focusBlock = value.focusBlock!;
        const { focusOffset } = selection;
        const nextText = document.getNextText(focusText.key)!;
        const isInVoid = document.hasVoidParent(focusText.key);
        const isNextInVoid = nextText && document.hasVoidParent(nextText.key);

        if (!isInVoid && focusOffset < focusText.text.length) {
            this.moveFocus(1);
            return this;
        }

        if (!nextText) {
            return this;
        }

        this.moveFocusToStartOf(nextText);

        if (!isInVoid && !isNextInVoid && focusBlock.hasNode(nextText.key)) {
            this.moveFocus(1);
        }

        return this;
    }

    /**
     * Add mark to text at `offset` and `length` in node by `key`.
     */
    public addMarkByKey(
        key: string,
        offset: number,
        length: number,
        mark: Mark,
        options: ChangeFlags = {}
    ): Change {
        mark = Mark.create(mark);
        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;
        const path = document.getPath(key)!;
        const node = document.getNode(key)!;
        const leaves = (node as Text).getLeaves();

        const operations: Operation[] = [];
        const bx = offset;
        const by = offset + length;
        let o = 0;

        leaves.forEach((leaf) => {
            const ax = o;
            const ay = ax + leaf.text.length;

            o += leaf.text.length;

            // If the leaf doesn't overlap with the operation, continue on.
            if (ay < bx || by < ax) {
                return;
            }

            // Otherwise, determine which offset and characters overlap.
            const start = Math.max(ax, bx);
            const end = Math.min(ay, by);

            // Handle marks with multiple data properties to preserve other properties
            const existingMark = leaf.marks.find((leafMark) => leafMark.type === mark.type);
            if (existingMark && mark.data && mark.data.size > 0) {
                const updatedData = existingMark.data.merge(mark.data);
                const updatedMark = existingMark.set('data', updatedData);

                if (updatedMark.equals(existingMark)) {
                    return;
                }

                operations.push(
                    Operation.create({
                        type: 'remove_mark',
                        path,
                        offset: start,
                        length: end - start,
                        mark: existingMark,
                    })
                );

                const shouldAddMark = updatedData.some((value) => value !== 'default');
                if (shouldAddMark) {
                    operations.push(
                        Operation.create({
                            type: 'add_mark',
                            path,
                            offset: start,
                            length: end - start,
                            mark: updatedMark,
                        })
                    );
                }

                return;
            }

            if (existingMark?.equals(mark)) {
                // If the leaf already has the mark, continue on.
                return;
            }

            operations.push(
                Operation.create({
                    type: 'add_mark',
                    path,
                    offset: start,
                    length: end - start,
                    mark,
                })
            );
        });

        this.applyOperations(operations);

        if (normalize) {
            const parent = document.getParent(key)!;
            this.normalizeNodeByKey(parent.key);
        }

        return this;
    }

    /**
     * Insert a `fragment` at `index` in a node by `key`.
     */
    public insertDocumentByKey(
        key: string,
        index: number,
        fragment: Document | Fragment,
        options: ChangeFlags = {}
    ): Change {
        const normalize = this.getFlag('normalize', options);

        fragment.nodes.forEach((node, i) => {
            this.insertNodeByKey(key, index + i, node, { normalize: false });
        });

        if (normalize) {
            this.normalizeNodeByKey(key);
        }

        return this;
    }

    /**
     * Set a `fragment` in a node by `key`.
     */
    public setFragmentByKey(
        parentKey: string,
        fragment: Fragment,
        options: ChangeFlags = {}
    ): Change {
        if (!Fragment.isFragment(fragment)) {
            return this;
        }

        const { value } = this;
        const { document } = value;

        // Check if the parent exists
        const parent = document.getNode(parentKey)!;
        if (!parent || (!parent.isBlock() && !parent.isInline())) {
            return this;
        }

        // Compute path of parent and fragment
        const parentPath = document.getPath(parentKey)!;
        const fragmentPath = document.getPath(fragment.key)!;
        const fragmentIndex = parent.getFragmentIndexFromPath(fragmentPath);

        if (!parentPath) {
            return this;
        }

        if (!fragmentPath) {
            return this.insertFragmentByKey(parentKey, fragment, undefined, options);
        }

        this.applyOperation(
            Operation.create({
                type: 'set_fragment',
                path: parentPath,
                fragmentPath,
                node: fragment,
                fragmentIndex,
            }),
            options
        );

        const normalize = this.getFlag('normalize', options);
        if (normalize) {
            this.normalizeNodeByKey(parentKey);
        }

        return this;
    }

    /**
     * Insert a `fragment` in a node by `key`.
     */
    public insertFragmentByKey(
        parentKey: string,
        fragment: Fragment,
        index: number | undefined = undefined,
        options: ChangeFlags = {}
    ): Change {
        if (!Fragment.isFragment(fragment)) {
            return this;
        }

        const { value } = this;
        const { document } = value;

        // Check if the parent exists
        const parent = document.getNode(parentKey)!;
        if (!parent || (!parent.isBlock() && !parent.isInline())) {
            return this;
        }

        // Compute path of parent
        const parentPath = document.getPath(parentKey)!;

        if (!parentPath) {
            return this;
        }

        // Compute insertion index and path of fragment
        const fragmentIndex = index === undefined ? parent.fragments.size : index;
        const fragmentPath = [...parentPath, parent.nodes.size + fragmentIndex];

        this.applyOperation(
            Operation.create({
                type: 'insert_fragment',
                path: parentPath,
                fragmentPath,
                node: fragment,
                fragmentIndex,
            }),
            options
        );

        const normalize = this.getFlag('normalize', options);
        if (normalize) {
            this.normalizeNodeByKey(parentKey);
        }

        return this;
    }

    /**
     * Insert a `node` at `index` in a node by `key`.
     */
    public insertNodeByKey(
        key: string,
        index: number,
        node: Text | Inline | Block,
        options: ChangeFlags = {}
    ): Change {
        // Don't allow Fragments to be inserted like Nodes.
        if (Fragment.isFragment(node)) {
            return this;
        }

        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;

        if (document.hasNodeKeyConflict(node)) {
            // This check is also done in the `insert_node` operation to be safe when receiving operations
            // from the network and applying them directly.
            // We still do the check here to avoid pushing an `insert_node` operation to the undo stack,
            // which could result in an invalid `remove_node` operation being pushed to the redo stack.
            logger.error(
                `skip insertNodeByKey, node "${node.key}" is conflicting with existing keys`
            );
            return this;
        }

        const path = document.getPath(key)!;
        if (!path) {
            return this;
        }

        this.applyOperation(
            Operation.create({
                type: 'insert_node',
                path: [...path, index],
                node,
            })
        );

        if (normalize) {
            this.normalizeNodeByKey(key);
        }

        return this;
    }

    /**
     * Insert a `node` after another one.
     */
    public insertNodeAfterByKey(
        key: string,
        node: Text | Inline | Block,
        options: ChangeFlags = {}
    ): Change {
        const { value } = this;
        const { document } = value;

        const parent = document.getParent(key)!;
        if (!parent) {
            return this;
        }
        const nodeIndex = parent.getChildIndex(key);

        return this.insertNodeByKey(parent.key, nodeIndex + 1, node, options);
    }

    /**
     * Insert a `node` before another one.
     */
    public insertNodeBeforeByKey(
        key: string,
        node: Text | Inline | Block,
        options: ChangeFlags = {}
    ): Change {
        const { value } = this;
        const { document } = value;

        const parent = document.getParent(key)!;
        if (!parent) {
            return this;
        }
        const nodeIndex = parent.getChildIndex(key);

        return this.insertNodeByKey(parent.key, nodeIndex, node, options);
    }

    /*
     * Insert `text` at `offset` in node by `key`.
     */
    public insertTextByKey(
        key: string,
        offset: number,
        text: string,
        marks?: MarkCreateSetProps,
        options: ChangeFlags = {}
    ): Change {
        const normalize = this.getFlag('normalize', options);

        const { value } = this;
        const { document } = value;
        const path = document.getPath(key)!;
        const node = document.getNode(key)!;
        const marksSet = marks ? Mark.createSet(marks) : (node as Text).getMarksAtIndex(offset);

        this.applyOperation(
            Operation.create({
                type: 'insert_text',
                path,
                offset,
                text,
                marks: marksSet,
            })
        );

        if (normalize) {
            const parent = document.getParent(key)!;
            this.normalizeNodeByKey(parent.key);
        }

        return this;
    }

    /**
     * Append a child `node` in a node by `key`.
     */
    public appendNodeByKey(
        key: string,
        node: Block | Inline | Text,
        options: ChangeFlags = {}
    ): Change {
        const { value } = this;
        const { document } = value;

        const index = (document.assertNode(key) as Block).nodes.size;
        this.insertNodeByKey(key, index, node, options);

        return this;
    }

    /**
     * Insert or set data in a Node.
     */
    public insertDataByKey(
        key: string,
        dataPath: string[],
        data: any,
        options: ChangeFlags = {}
    ): Change {
        const normalize = this.getFlag('normalize', options);

        const { value } = this;
        const { document } = value;
        const path = document.getPath(key)!;
        const node = document.assertNode(key);

        this.applyOperation(
            Operation.create({
                type: 'set_data',
                path,
                dataPath,
                data,
                previousData: (node as Block).data.getIn(dataPath),
            }),
            options
        );

        if (normalize) {
            this.normalizeNodeByKey(key);
        }

        return this;
    }

    /**
     * Set a data in a node.
     */
    public setDataByKey(
        key: string,
        dataPath: string[],
        data: any,
        options: ChangeFlags = {}
    ): Change {
        return this.insertDataByKey(key, dataPath, data, options);
    }

    /**
     * Insert an element into an array.
     */
    public insertArrayDataByKey(
        key: string,
        dataPath: string[],
        data: any,
        index: number,
        options: ChangeFlags = {}
    ): Change {
        const normalize = this.getFlag('normalize', options);

        const { value } = this;
        const { document } = value;
        const path = document.getPath(key)!;

        this.applyOperation(
            Operation.create({
                type: 'insert_array_data',
                path,
                dataPath,
                data,
                dataIndex: index,
            }),
            options
        );

        if (normalize) {
            this.normalizeNodeByKey(key);
        }

        return this;
    }

    /**
     * Remove an element from an array.
     */
    public removeArrayDataByKey(
        key: string,
        dataPath: string[],
        data: any,
        options: ChangeFlags = {}
    ): Change {
        const normalize = this.getFlag('normalize', options);

        const { value } = this;
        const { document } = value;
        const path = document.getPath(key)!;
        const node = document.assertNode(key);
        const arrayData = (node as Block).data.getIn(dataPath);

        if (!Array.isArray(arrayData)) {
            return this;
        }

        const index = arrayData.indexOf(data);
        if (index < 0) {
            return this;
        }

        this.applyOperation(
            Operation.create({
                type: 'remove_array_data',
                path,
                dataPath,
                data,
                dataIndex: index,
            }),
            options
        );

        if (normalize) {
            this.normalizeNodeByKey(key);
        }

        return this;
    }

    /**
     * Remove data from a Node by its path.
     */
    public removeDataPathByKey(key: string, dataPath: string[], options: ChangeFlags = {}): Change {
        const normalize = this.getFlag('normalize', options);

        const { value } = this;
        const { document } = value;
        const path = document.getPath(key)!;
        const node = document.assertNode(key);

        this.applyOperation(
            Operation.create({
                type: 'remove_data',
                path,
                dataPath,
                data: (node as Block).data.getIn(dataPath),
            })
        );

        if (normalize) {
            this.normalizeNodeByKey(key);
        }

        return this;
    }

    /**
     * Merge a node by `key` with the previous node.
     */
    public mergeNodeByKey(key: string, options: ChangeFlags = {}): Change {
        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;
        const path = document.getPath(key)!;
        const original = document.getDescendant(key)!;
        const previous = document.getPreviousSibling(key)!;

        if (!previous) {
            throw new Error(`Unable to merge node with key "${key}", no previous key.`);
        }

        const position = previous.object === 'text' ? previous.text.length : previous.nodes.size;

        this.applyOperation(
            Operation.create({
                type: 'merge_node',
                path,
                position,
                // for undos to succeed we only need the type and data because
                // these are the only properties that get changed in the merge operation
                properties: {
                    key: original.key,
                    type: original.type,
                    data: (original as Block).data,
                },
                target: null,
            })
        );

        if (normalize) {
            const parent = document.getParent(key)!;
            this.normalizeNodeByKey(parent.key);
        }

        return this;
    }

    /**
     * Move a node by `key` to a new parent by `newKey` and `index`.
     * `newKey` is the key of the container (it can be the document itself)
     */
    public moveNodeByKey(
        key: string,
        newKey: string,
        newIndex: number,
        options: ChangeFlags = {}
    ): Change {
        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;

        const path = document.getPath(key)!;
        assert(path, `The node with key "${key}" could not be found in the document.`);

        const newPath = document.getPath(newKey)!;
        assert(newPath, `The node with key "${newKey}" could not be found in the document.`);

        this.applyOperation(
            Operation.create({
                type: 'move_node',
                path,
                newPath: [...newPath, newIndex],
            })
        );

        if (normalize) {
            const parent = document.getCommonAncestor(key, newKey)!;
            assert(parent, `The node with key "${key}" has no common ancestor with "${newKey}".`);
            this.normalizeNodeByKey(parent.key);
        }

        return this;
    }

    /**
     * Move after or before another node.
     */
    public moveAfterOrBefore(
        key: string,
        toKey: string,
        move: number,
        options: ChangeFlags = {}
    ): Change {
        const { value } = this;
        const { document } = value;

        const parent = document.getParent(toKey)!;
        if (!parent) {
            return this;
        }

        let toIndex = parent.getChildIndex(toKey) + move;
        const fromIndex = parent.getChildIndex(key);

        if (toIndex === fromIndex) {
            return this;
        }

        if (fromIndex >= 0) {
            toIndex = fromIndex <= toIndex ? toIndex - 1 : toIndex;
        }

        return this.moveNodeByKey(key, parent.key, toIndex, options);
    }

    public moveAfterNodeByKey(key: string, toKey: string, options: ChangeFlags = {}): Change {
        return this.moveAfterOrBefore(key, toKey, 1, options);
    }

    public moveBeforeNodeByKey(key: string, toKey: string, options: ChangeFlags = {}): Change {
        return this.moveAfterOrBefore(key, toKey, 0, options);
    }

    /**
     * Move a block range selection from current position to a new position.
     */
    public moveBlockRangeSelectionTo(
        targetKey: string,
        targetIndexOffset = 0,
        options: ChangeFlags = {}
    ): Change {
        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { selection, document } = value;

        if (!isBlockRangeSelection(selection)) {
            return this;
        }

        // Use the literal selected blocks rather than `value.blocks`, which
        // expands a single-block selection to the highest sole-child ancestor
        // (or collapses a multi-block range to a common ancestor). For moves,
        // we want to operate on exactly what was selected and leave the
        // enclosing container in place to be normalized by the schema.
        const blocks = selection.getBlocksLiteral(document);

        blocks.reverse().forEach((block) => {
            this.removeNodeByKey(block.key, { normalize: false });
        });

        const parent = this.value.document.getParent(targetKey)!;
        if (!parent) {
            return this;
        }

        const index = parent.getChildIndex(targetKey);

        blocks.reverse().forEach((block) => {
            this.insertNodeByKey(parent.key, index + targetIndexOffset, block, {
                normalize: false,
            });
        });

        if (normalize) {
            this.normalizeNodeByKey(parent.key);
        }

        return this;
    }

    /**
     * Remove mark from text at `offset` and `length` in node by `key`.
     */
    public removeMarkByKey(
        key: string,
        offset: number,
        length: number,
        mark: Mark,
        options: ChangeFlags = {},
        markOptions: MarkOptions = {}
    ): Change {
        mark = Mark.create(mark);
        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;
        const path = document.getPath(key)!;
        const node = document.getNode(key)!;
        const leaves = (node as Text).getLeaves();

        const operations: Operation[] = [];
        const bx = offset;
        const by = offset + length;
        let o = 0;

        leaves.forEach((leaf) => {
            const ax = o;
            const ay = ax + leaf.text.length;

            o += leaf.text.length;

            // If the leaf doesn't overlap with the operation, continue on.
            if (ay < bx || by < ax) {
                return;
            }

            // Otherwise, determine which offset and characters overlap.
            const start = Math.max(ax, bx);
            const end = Math.min(ay, by);

            // Handle marks with multiple data properties to preserve other properties
            if (markOptions.property) {
                const existingMark = leaf.marks.find((leafMark) => leafMark.type === mark.type);
                if (!existingMark || !existingMark.data.has(markOptions.property)) {
                    return;
                }

                const updatedData = existingMark.data.set(markOptions.property, 'default');
                const updatedMark = existingMark.set('data', updatedData);

                if (updatedMark.equals(existingMark)) {
                    return;
                }

                operations.push(
                    Operation.create({
                        type: 'remove_mark',
                        path,
                        offset: start,
                        length: end - start,
                        mark: existingMark,
                    })
                );

                const shouldAddMark = updatedData.some((value) => value !== 'default');
                if (shouldAddMark) {
                    operations.push(
                        Operation.create({
                            type: 'add_mark',
                            path,
                            offset: start,
                            length: end - start,
                            mark: updatedMark,
                        })
                    );
                }

                return;
            }

            // If the mark is not here, continue on.
            if (!leaf.marks.has(mark)) {
                return;
            }

            operations.push(
                Operation.create({
                    type: 'remove_mark',
                    path,
                    offset: start,
                    length: end - start,
                    mark,
                })
            );
        });

        this.applyOperations(operations);

        if (normalize) {
            const parent = document.getParent(key)!;
            this.normalizeNodeByKey(parent.key);
        }

        return this;
    }

    public removeInvalidMarksByKey(
        key: string,
        validMarks: string[],
        options: ChangeFlags = {}
    ): Change {
        const { value } = this;
        const { document } = value;
        const node = document.getNode(key)!;

        const leaves = (node as Text).getLeaves();
        if (!leaves.size) {
            return this;
        }

        const unknownMarks = node
            .getMarksAsArray()
            .filter((mark: Mark) => !validMarks.includes(mark.type));
        if (!unknownMarks || !unknownMarks.length) {
            return this;
        }

        let offset = 0;
        leaves.forEach((leaf: Leaf) => {
            leaf.marks.forEach((mark: Mark) => {
                if (!validMarks.includes(mark.type)) {
                    this.removeMarkByKey(node.key, offset, leaf.text.length, mark, options);
                }
            });

            offset += leaf.text.length;
        });

        return this;
    }

    public removeAllMarksByKey(key: string, options: ChangeFlags = {}): Change {
        const { value } = this;
        const { document } = value;
        const node = document.getNode(key)!;
        const texts = node.object === 'text' ? [node] : node.getTextsAsArray(false);

        texts.forEach((text) => {
            text.getMarksAsArray().forEach((mark) => {
                this.removeMarkByKey(text.key, 0, text.text.length, mark, options);
            });
        });

        return this;
    }

    /**
     * Remove "keys" from "node" data
     */
    public removeDataByKey(key: string, keys: string[], options: ChangeFlags = {}): Change {
        const normalize = this.getFlag('normalize', options);

        keys.forEach((propKey) => {
            this.removeDataPathByKey(key, [propKey], { normalize: false });
        });

        if (normalize) {
            this.normalizeNodeByKey(key);
        }

        return this;
    }

    /**
     * Remove a node by `key`.
     */
    public removeNodeByKey(key: string, options: ChangeFlags = {}): Change {
        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;
        const path = document.getPath(key)!;
        const node = document.getNode(key)!;

        // Prevent the removal of the root node.
        if (!node || !path) {
            return this;
        }

        switch (node.object) {
            // Prevent the removal of the document node.
            case 'document': {
                return this;
            }
            case 'fragment': {
                const parent = document.getParent(key)!;
                if (!parent) {
                    return this;
                }

                return this.removeFragmentInParentByKey(parent.key, node.fragmentKey, options);
            }
        }

        this.applyOperation(
            Operation.create({
                type: 'remove_node',
                path,
                node,
            })
        );

        if (normalize) {
            const parent = document.getParent(key)!;
            assertIsDefined(parent, 'Parent not found for node');

            this.normalizeNodeByKey(parent.key);

            // Deleting a node may generate an unset selection at the operation level before the parent is normalized
            // ex: when deleting the only block in a document
            // An unset selection is not a critical issue but doesn't provide the best UX (require new focus)
            // In that case we nornalize the selection
            if (TextRange.isTextRange(this.value.selection) && this.value.selection.isUnset) {
                this.normalizeSelection();
            }
        }

        return this;
    }

    /**
     * Remove a fragment by its unique `key` in the document.
     */
    public removeFragmentByKey(key: string, options: ChangeFlags = {}): Change {
        const { value } = this;
        const { document } = value;

        const fragment = document.getNode(key)!;
        if (!fragment || fragment.object !== 'fragment') {
            return this;
        }

        const parent = document.getParent(key)!;
        if (!parent) {
            throw new Error('Parent not found for fragment');
        }

        return this.removeFragmentInParentByKey(parent.key, fragment.fragmentKey, options);
    }

    /**
     * Remove a fragment by its `fragmentKey` in a parent
     */
    public removeFragmentInParentByKey(
        parentKey: string,
        fragmentKey: string,
        options: ChangeFlags = {}
    ): Change {
        const { value } = this;
        const { document, selection } = value;

        // Check if the parent exists
        const parent = document.getNode(parentKey)!;
        if (!parent || (parent.object !== 'block' && parent.object !== 'inline')) {
            return this;
        }

        // Check if the fragment exists
        const fragment = parent.getFragmentByKey(fragmentKey);
        if (!fragment || fragment.object !== 'fragment') {
            return this;
        }

        // Compute path of parent and fragment
        const parentPath = document.getPath(parent.key)!;
        const fragmentPath = document.getPath(fragment.key)!;
        const fragmentIndex = parent.getFragmentIndexFromPath(fragmentPath);

        this.applyOperation(
            Operation.create({
                type: 'remove_fragment',
                path: parentPath,
                fragmentPath,
                node: fragment,
                fragmentIndex,
            })
        );

        const normalize = this.getFlag('normalize', options);
        if (normalize) {
            this.normalizeNodeByKey(parent.key);

            // Normalize the selection if it was lost
            if (selection.isSet && this.value.selection.isUnset) {
                this.normalizeSelection();
            }
        }

        return this;
    }

    /**
     * Insert `text` at `offset` in node by `key`.
     */
    public setTextByKey(
        key: string,
        text: string,
        marks?: MarkCreateSetProps,
        options: ChangeFlags = {}
    ): Change {
        const textNode = this.value.document.getDescendant(key)!;
        this.replaceTextByKey(key, 0, textNode.text.length, text, marks, options);
        return this;
    }

    /**
     * Replace A Length of Text with another string or text
     */
    public replaceTextByKey(
        key: string,
        offset: number,
        length: number,
        text: string,
        marksProps?: MarkCreateSetProps,
        options: ChangeFlags = {}
    ): Change {
        const { document } = this.value;
        const textNode = document.getDescendant(key)!;

        if (length + offset > textNode.text.length) {
            length = textNode.text.length - offset;
        }

        const range = TextRange.create({
            anchorKey: key,
            focusKey: key,
            anchorOffset: offset,
            focusOffset: offset + length,
        });
        let activeMarks = document.getActiveMarksAtRange(range);

        this.removeTextByKey(key, offset, length, { normalize: false });

        let marks = marksProps ? Mark.createSet(marksProps) : activeMarks;
        if (activeMarks) {
            // Do not use `has` because we may want to reset marks like font-size with an updated data;
            activeMarks = activeMarks.filter(
                (activeMark) => !marks.find((m) => activeMark.type === m.type)
            );

            marks = activeMarks.union(marks);
        }

        this.insertTextByKey(key, offset, text, marks, options);

        return this;
    }

    /**
     * Remove text at `offset` and `length` in node by `key`.
     */
    public removeTextByKey(
        key: string,
        offset: number,
        length: number,
        options: ChangeFlags = {}
    ): Change {
        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;
        const path = document.getPath(key)!;
        const node = document.getNode(key)!;
        const leaves = (node as Text).getLeaves();
        const { text } = node;

        const removals: Operation[] = [];
        const bx = offset;
        const by = offset + length;
        let o = 0;

        leaves.forEach((leaf) => {
            const ax = o;
            const ay = ax + leaf.text.length;

            o += leaf.text.length;

            // If the leaf doesn't overlap with the removal, continue on.
            if (ay < bx || by < ax) {
                return;
            }

            // Otherwise, determine which offset and characters overlap.
            const start = Math.max(ax, bx);
            const end = Math.min(ay, by);
            const textContent = text.slice(start, end);

            removals.push(
                Operation.create({
                    type: 'remove_text',
                    path,
                    offset: start,
                    text: textContent,
                    marks: leaf.marks,
                })
            );
        });

        // Apply in reverse order, so subsequent removals don't impact previous ones.
        this.applyOperations(removals.reverse());

        if (normalize) {
            const block = document.getClosestBlock(key)!;
            this.normalizeNodeByKey(block.key);
        }

        return this;
    }

    /**
     * Replace a `node` with another `node`
     */
    public replaceNodeByKey(
        key: string,
        newNode: Block | Fragment | Inline | Text | Document,
        options: ChangeFlags = {}
    ): Change {
        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;
        const node = document.assertNode(key);
        const parent = document.getParent(key)!;

        if (!parent) {
            throw new Error(`Parent not found for node "${key}"`);
        }

        if (node.object === 'fragment') {
            if (newNode.object !== 'fragment') {
                throw new Error('Replacing a fragment with a non-fragment is not allowed');
            }

            this.removeFragmentByKey(key, { normalize: false });
            // TODO: reuse previous index instead of appending at the end
            this.insertFragmentByKey(parent.key, newNode, undefined, { normalize: false });
        } else {
            const index = parent.getChildIndex(node.key);
            this.removeNodeByKey(key, { normalize: false });
            this.insertNodeByKey(parent.key, index, newNode as ChildNode, { normalize: false });
        }

        if (normalize) {
            this.normalizeNodeByKey(parent.key);
        }

        return this;
    }

    /**
     * Replace a `node` with a fragment
     */
    public replaceWithDocumentByKey(
        key: string,
        fragment: Document | Fragment,
        options: ChangeFlags = {}
    ): Change {
        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;
        const node = document.assertNode(key);
        const parent = document.getParent(key)!;
        assertIsDefined(parent);

        const index = parent.getChildIndex(node.key);
        this.removeNodeByKey(key, { normalize: false });

        this.insertDocumentByKey(parent.key, index, fragment, { normalize: false });

        if (normalize) {
            this.normalizeNodeByKey(parent.key);
        }

        return this;
    }

    /**
     * Replace the whole content of the document with a new document.
     * The approach is optimized to limit the amount of operations.
     */
    public replaceAllBlocks(
        document: Document,
        options: ChangeFlags & {
            /**
             * If true, the selection will be moved as a block selection of the last inserted/updated block.
             */
            moveSelectionToLastBlock?: boolean;
        } = {}
    ): Change {
        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document: previousDocument } = value;
        const documentKey = previousDocument.key;

        // Set of keys that must remain (or be created) in the final document.
        const targetKeys = new Set(document.nodes.map((node) => node.key));

        // 1. Remove every current top-level block whose key is not present in the
        //    target document. Doing all the removals *before* any insertion is what
        //    keeps a moved block safe: when a block keeps its key but changes
        //    position, its previous occurrence is gone before it is re-inserted at
        //    its new position, so `insert_node` never sees a transient duplicate key
        //    and silently drops the block (the "skip insert_node, node is
        //    conflicting with existing keys" error).
        previousDocument.nodes.toArray().forEach((node) => {
            if (!targetKeys.has(node.key)) {
                this.removeNodeByKey(node.key, { normalize: false });
            }
        });

        let lastBlockKey: string | undefined;

        // 2. Reconcile the surviving blocks against the target order and content.
        //    At iteration `index`, positions [0, index) already hold the correct
        //    target blocks, so the block we need next (if it already exists) is
        //    always located at a position >= index — i.e. a backward move — which
        //    never collides with an already-placed block.
        //
        //    `lastBlockKey` is only set when a block is actually inserted, moved or
        //    updated, so `moveSelectionToLastBlock` collapses the selection onto a
        //    block this call touched rather than onto a trailing untouched block.
        document.nodes.forEach((targetNode, index) => {
            const currentAtIndex = this.value.document.nodes.get(index);

            if (currentAtIndex && currentAtIndex.key === targetNode.key) {
                // Right block already at the right place: only update its content.
                if (!currentAtIndex.equals(targetNode)) {
                    this.replaceNodeByKey(targetNode.key, targetNode, { normalize: false });
                    lastBlockKey = targetNode.key;
                }
                return;
            }

            const existing = this.value.document.nodes.find((node) => node.key === targetNode.key);

            if (existing) {
                // The block survived but sits at the wrong position: move it into
                // place, then update its content if it changed.
                this.moveNodeByKey(targetNode.key, documentKey, index, { normalize: false });
                const moved = this.value.document.nodes.get(index);
                if (moved && !moved.equals(targetNode)) {
                    this.replaceNodeByKey(targetNode.key, targetNode, { normalize: false });
                }
                lastBlockKey = targetNode.key;
                return;
            }

            // Brand new block: insert it at its target position.
            this.insertNodeByKey(documentKey, index, targetNode, { normalize: false });
            lastBlockKey = targetNode.key;
        });

        if (lastBlockKey && options.moveSelectionToLastBlock) {
            const lastBlock = this.value.document.getNode(lastBlockKey)!;
            if (lastBlock) {
                this.collapseToEndOf(lastBlock);
            }
        }

        if (normalize) {
            this.normalizeDocument();
        }

        return this;
    }

    /**
     * Set `properties` on a node by `key`.
     */
    public setNodeByKey(
        key: string,
        properties: BlockCreateProps | InlineCreateProps = {},
        options: ChangeFlags = {}
    ): Change {
        properties = createNodeProperties(properties);
        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;
        const path = document.getPath(key)!;
        const node = document.getNode(key)!;

        this.applyOperation(
            Operation.create({
                type: 'set_node',
                path,
                node: node as ChildNode,
                properties,
            })
        );

        if (normalize) {
            this.normalizeNodeByKey(node.key);
        }

        return this;
    }

    /**
     * Split a node by `key` at `position`.
     */
    public splitNodeByKey(key: string, position: number, options: ChangeFlags = {}): Change {
        const { normalize = true, target = null } = options;
        const { value } = this;
        const { document } = value;
        const path = document.getPath(key)!;
        const node = document.getDescendantAtPath(path)!;

        // Generate a new key for the node after the split.
        const splitKey = generateKey();

        this.applyOperation(
            Operation.create({
                type: 'split_node',
                path,
                position,
                properties: {
                    key: splitKey,
                    type: node.type,
                    data: (node as Block).data,
                },
                target,
            })
        );

        if (normalize) {
            const parent = document.getParent(key)!;
            this.normalizeNodeByKey(parent.key);
        }

        return this;
    }

    /**
     * Split a node deeply down the tree by `key`, `textKey` and `textOffset`.
     */
    public splitDescendantsByKey(
        key: string,
        textKey: string,
        textOffset: number,
        options: ChangeFlags = {}
    ): Change {
        if (key === textKey) {
            this.splitNodeByKey(textKey, textOffset, options);
            return this;
        }

        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;

        const text = document.getNode(textKey)!;
        const ancestors = document.getAncestors(textKey)!;
        const nodes = ancestors
            .skipUntil((a) => a.key === key)
            .reverse()
            .unshift(text as AncestorNode);
        let previous;
        let index: number;

        nodes.forEach((node) => {
            const prevIndex = index == null ? null : index;
            index = previous ? node.getChildIndex(previous.key) + 1 : textOffset;
            previous = node;

            this.splitNodeByKey(node.key, index, {
                normalize: false,
                target: prevIndex,
            });
        });

        if (normalize) {
            const parent = document.getParent(key)!;
            this.normalizeNodeByKey(parent.key);
        }

        return this;
    }

    /**
     * Unwrap content from an inline parent with `properties`.
     */
    public unwrapInlineByKey(key: string, options: ChangeFlags = {}): Change {
        const { value } = this;
        const { document, selection } = value;
        const node = document.assertDescendant(key);
        assert(Inline.isInline(node), `unwrapInlineByKey called on ${node.object}`);

        // unwrapInlineAtRange only supports TextRanges at the moment, so convert to text selection and use from there.
        const range: TextRange = (() => {
            if (isBlockRangeSelection(selection)) {
                this.switchSelection();
                return this.value.textSelection;
            }

            const first = node.getFirstText()!;
            const last = node.getLastText()!;
            return selection.moveToRangeOf(first, last);
        })();

        this.unwrapInlineAtRange(range, node, options);

        return this;
    }

    /**
     * Unwrap content from a block parent with `properties`.
     */
    public unwrapBlockByKey(
        key: string,
        properties: BlockCreateProps | InlineCreateProps = {},
        options: ChangeFlags = {}
    ): Change {
        const { value } = this;
        const { document, selection } = value;
        const node = document.assertDescendant(key);
        assert(Block.isBlock(node), `unwrapBlockByKey called on ${node.object}`);

        const range: TextRange = (() => {
            // unwrapBlockAtRange only supports TextRanges at the moment, so convert to text selection and use from there.
            if (isBlockRangeSelection(selection)) {
                this.switchSelection();
                return this.value.textSelection;
            }

            const first = node.getFirstText()!;
            const last = node.getLastText()!;
            return selection.moveToRangeOf(first, last);
        })();

        this.unwrapBlockAtRange(range, properties, options);
        return this;
    }

    /**
     * Unwrap a single node from its parent.
     *
     * If the node is surrounded with siblings, its parent will be
     * split. If the node is the only child, the parent is removed, and
     * simply replaced by the node itself.  Cannot unwrap a root node.
     */
    public unwrapNodeByKey(key: string, options: ChangeFlags = {}): Change {
        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;
        const parent = document.getParent(key)!;

        // Fragments and their first-level children should not be unwrapped.
        if (
            !parent ||
            Fragment.isFragment(parent) ||
            (parent.isBlock() && parent.hasFragment(key))
        ) {
            return this;
        }

        const node = parent.getChild(key)!;
        const index = parent.getChildIndex(node.key);
        const isFirst = index === 0;
        const isLast = index === parent.nodes.size - 1;

        const parentParent = document.getParent(parent.key)!;
        const parentIndex = parentParent.getChildIndex(parent.key);

        if (parent.nodes.size === 1) {
            this.moveNodeByKey(key, parentParent.key, parentIndex, {
                normalize: false,
            });

            this.removeNodeByKey(parent.key, options);
        } else if (isFirst) {
            // Just move the node before its parent.
            this.moveNodeByKey(key, parentParent.key, parentIndex, options);
        } else if (isLast) {
            // Just move the node after its parent.
            this.moveNodeByKey(key, parentParent.key, parentIndex + 1, options);
        } else {
            // Split the parent.
            this.splitNodeByKey(parent.key, index, { normalize: false });

            // Extract the node in between the splitted parent.
            this.moveNodeByKey(key, parentParent.key, parentIndex + 1, {
                normalize: false,
            });

            if (normalize) {
                this.normalizeNodeByKey(parentParent.key);
            }
        }

        return this;
    }

    /**
     * Wrap a node in a block with `properties`.
     */
    public wrapBlockByKey(key: string, attrs: BlockCreateProps, options: ChangeFlags = {}): Change {
        let block = Block.create(attrs);
        block = block.set('nodes', block.nodes.clear());

        const { document } = this.value;
        const node = document.assertDescendant(key);

        if (node.object === 'fragment') {
            return this;
        }

        const parent = document.getParent(node.key)!;
        const index = parent.getChildIndex(node.key);

        this.insertNodeByKey(parent.key, index, block, { normalize: false });
        this.moveNodeByKey(node.key, block.key, 0, options);

        return this;
    }

    /**
     * Wrap a node in an inline with `properties`.
     */
    public wrapInlineByKey(
        key: string,
        attrs: InlineCreateProps,
        options: ChangeFlags = {}
    ): Change {
        let inline = Inline.create(attrs);
        inline = inline.set('nodes', inline.nodes.clear());

        const { document } = this.value;
        const node = document.assertDescendant(key);
        const parent = document.getParent(node.key)!;
        const index = parent.getChildIndex(node.key);

        this.insertNodeByKey(parent.key, index, inline, { normalize: false });
        this.moveNodeByKey(node.key, inline.key, 0, options);

        return this;
    }

    /**
     * Add a new `mark` to the characters at `range`.
     */
    public addMarkAtRange(range: TextRange, mark, options: ChangeFlags = {}): Change {
        if (range.isCollapsed) {
            return this;
        }

        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;
        const { focusKey, startKey, startOffset, endKey, endOffset } = range as SetTextRange;

        const fragment = focusKey ? document.getFragmentParent(focusKey) : null;
        const texts = fragment ? fragment.getTextsAtRange(range) : document.getTextsAtRange(range);

        texts.forEach((node) => {
            const { key } = node;
            let index = 0;
            let { length } = node.text;

            if (key === startKey) {
                index = startOffset;
            }
            if (key === endKey) {
                length = endOffset;
            }
            if (key === startKey && key === endKey) {
                length = endOffset - startOffset;
            }

            this.addMarkByKey(key, index, length, mark, { normalize });
        });

        return this;
    }

    /**
     * Add a list of `marks` to the characters at `range`.
     */
    public addMarksAtRange(range: TextRange, marks, options: ChangeFlags = {}): Change {
        marks.forEach((mark) => this.addMarkAtRange(range, mark, options));
        return this;
    }

    /**
     * Delete everything in a `range`.
     */
    public deleteAtRange(range: SelectionRange, options: ChangeFlags = {}): Change {
        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        let { document } = value;

        if (isBlockRangeSelection(range)) {
            return this.deleteBlockRange(range, options);
        }

        if (range.isCollapsed) {
            return this;
        }

        // Snapshot the selection, which creates an extra undo save point, so that
        // when you undo a delete, the expanded selection will be retained.
        this.snapshotSelection();

        // An expanded (non-collapsed) range always has resolved start/end keys.
        let { startOffset, endOffset } = range;
        let startKey = range.startKey!;
        let endKey = range.endKey!;
        let isStartVoid = document.hasVoidParent(startKey);
        let isEndVoid = document.hasVoidParent(endKey);
        let startBlock = document.getClosestBlock(startKey)!;
        let endBlock = document.getClosestBlock(endKey)!;

        // Check if we have a "hanging" selection case where even though the
        // selection extends into the start of the end node, we actually want to
        // ignore that for UX reasons.
        const isHanging =
            startOffset === 0 &&
            endOffset === 0 &&
            isStartVoid === false &&
            startKey === startBlock.getFirstText()!.key &&
            endKey === endBlock.getFirstText()!.key;

        // If the start node is inside a void node, remove the void node and update
        // the starting point to be right after it, continuously until the start point
        // is not a void, or until the entire range is handled.
        while (isStartVoid) {
            const startVoid = document.getClosestVoid(startKey)!;
            const nextSibling = document.getNextSibling(startVoid.key)!;
            this.removeNodeByKey(startVoid.key, { normalize: false });

            // If the start and end keys are the same, we're done.
            if (startKey === endKey) {
                return this;
            }

            if (!nextSibling) {
                return this;
            }

            const nextText = nextSibling.isText() ? nextSibling : nextSibling.getFirstText();

            // If there is no next text node, we're done.
            if (!nextText) {
                return this;
            }
            // Continue...
            document = this.value.document;
            startKey = nextText.key;
            startOffset = 0;
            isStartVoid = document.hasVoidParent(startKey);
        }

        // If the end node is inside a void node, do the same thing but backwards. But
        // we don't need any aborting checks because if we've gotten this far there
        // must be a non-void node that will exit the loop.
        while (isEndVoid) {
            const endVoid = document.getClosestVoid(endKey)!;
            const prevText = document.getPreviousText(endKey)!;
            this.removeNodeByKey(endVoid.key, { normalize: false });

            // Continue...
            document = this.value.document;
            endKey = prevText.key;
            endOffset = prevText.text.length;
            isEndVoid = document.hasVoidParent(endKey);
        }

        // If the start and end key are the same, and it was a hanging selection, we
        // can just remove the entire block.
        if (startKey === endKey && isHanging) {
            this.removeNodeByKey(startBlock.key, { normalize: false });
        } else if (startKey === endKey) {
            // Otherwise, if it wasn't hanging, we're inside a single text node, so we can
            // simply remove the text in the range.
            const index = startOffset;
            const length = endOffset - startOffset;
            this.removeTextByKey(startKey, index, length, { normalize: false });
        } else {
            // Otherwise, we need to recursively remove text and nodes inside the start
            // block after the start offset and inside the end block before the end
            // offset. Then remove any blocks that are in between the start and end
            // blocks. Then finally merge the start and end nodes.
            startBlock = document.getClosestBlock(startKey)!;
            endBlock = document.getClosestBlock(endKey)!;
            const startText = document.getNode(startKey)!;
            const endText = document.getNode(endKey)!;
            const startLength = startText.text.length - startOffset;
            const endLength = endOffset;

            const ancestor = document.getCommonAncestor(startKey, endKey)!;
            const startChild = (ancestor as Block).getFurthestAncestor(startKey)!;
            const endChild = (ancestor as Block).getFurthestAncestor(endKey)!;

            const startParent = document.getParent(startBlock.key)!;
            const startParentIndex = startParent.getChildIndex(startBlock.key);
            const endParentIndex = startParent.getChildIndex(endBlock.key);

            let child;

            // Iterate through all of the nodes in the tree after the start text node
            // but inside the end child, and remove them.
            child = startText;

            while (child.key !== startChild.key) {
                const parent = document.getParent(child.key)!;
                const index = parent.getChildIndex(child.key);
                const afters = parent.nodes.slice(index + 1);

                afters.reverse().forEach((node) => {
                    this.removeNodeByKey(node.key, { normalize: false });
                });

                child = parent;
            }

            // Remove all of the middle children.
            const startChildIndex = (ancestor as Block).getChildIndex(startChild.key);
            const endChildIndex = (ancestor as Block).getChildIndex(endChild.key);
            const middles = (ancestor as Block).nodes.slice(startChildIndex + 1, endChildIndex);

            middles.reverse().forEach((node) => {
                this.removeNodeByKey(node.key, { normalize: false });
            });

            // Remove the nodes before the end text node in the tree.
            child = endText;

            while (child.key !== endChild.key) {
                const parent = document.getParent(child.key)!;
                const index = parent.getChildIndex(child.key);
                const befores = parent.nodes.slice(0, index);

                befores.reverse().forEach((node) => {
                    this.removeNodeByKey(node.key, { normalize: false });
                });

                child = parent;
            }

            // Remove any overlapping text content from the leaf text nodes.
            if (startLength !== 0) {
                this.removeTextByKey(startKey, startOffset, startLength, {
                    normalize: false,
                });
            }

            if (endLength !== 0) {
                this.removeTextByKey(endKey, 0, endOffset, { normalize: false });
            }

            // If the start and end blocks aren't the same, move and merge the end block
            // into the start block.
            if (startBlock.key !== endBlock.key) {
                document = this.value.document;
                const lonely = document.getFurthestOnlyChildAncestor(endBlock.key);

                // Move the end block to be right after the start block.
                if (endParentIndex !== startParentIndex + 1) {
                    this.moveNodeByKey(endBlock.key, startParent.key, startParentIndex + 1, {
                        normalize: false,
                    });
                }

                // If the selection is hanging, just remove the start block, otherwise
                // merge the end block into it.
                if (isHanging) {
                    this.removeNodeByKey(startBlock.key, { normalize: false });
                } else {
                    this.mergeNodeByKey(endBlock.key, { normalize: false });
                }

                // If nested empty blocks are left over above the end block, remove them.
                if (lonely) {
                    this.removeNodeByKey(lonely.key, { normalize: false });
                }
            }
        }

        // If we should normalize, do it on the whole document, after everything.
        if (normalize) {
            this.normalizeDocument();
        }

        return this;
    }

    /*
     * Delete block range and move the selection forward.
     */
    public deleteBlockRangeForward(options: ChangeFlags = {}): Change {
        const normalize = this.getFlag('normalize', options);
        const { document, selection } = this.value;
        if (!isBlockRangeSelection(selection)) {
            return this;
        }
        const next = selection.moveSelectionForward(document);
        this.deleteBlockRange(selection, { normalize: false });
        const isMoved =
            next.anchorBlock !== selection.anchorBlock && next.focusBlock !== selection.focusBlock;
        // If the selection has moved, we can collapse to the start of the block.
        if (isMoved) {
            this.select(next).switchSelection().collapseToStartOfBlock().focus();
        } else {
            // Moving forward hasn't worked so the deleted block is likely the last in the document.
            // Find the last text node of the document to move to.
            const text = this.value.document.getLastText();
            if (text) {
                this.selectText(
                    TextRange.create({
                        anchorKey: text.key,
                        anchorOffset: 0,
                        focusKey: text.key,
                        focusOffset: 0,
                        isFocused: true,
                    })
                )
                    .collapseToEndOfBlock()
                    .focus();
            }
        }

        // If we should normalize, do it on the whole document, after everything.
        if (normalize) {
            this.normalizeDocument();
        }

        return this;
    }

    /**
     *  Delete block range and move the selection backward.
     */
    public deleteBlockRangeBackward(options: ChangeFlags = {}) {
        const normalize = this.getFlag('normalize', options);
        const { document, selection } = this.value;
        if (!isBlockRangeSelection(selection)) {
            return this;
        }

        const next = selection.moveSelectionBackward(document);
        this.deleteBlockRange(selection, { normalize: false });
        const isMoved =
            next.anchorBlock !== selection.anchorBlock && next.focusBlock !== selection.focusBlock;
        // If the selection has moved, we can collapse to the end of the block.
        if (isMoved) {
            this.select(next).switchSelection().collapseToEndOfBlock().focus();
        } else {
            // Moving backward hasn't worked so the deleted block is likely the first in the document.
            // Find the first text node of the document to move to.
            const text = this.value.document.getFirstText();
            if (text) {
                this.selectText(
                    TextRange.create({
                        anchorKey: text?.key,
                        anchorOffset: 0,
                        focusKey: text?.key,
                        focusOffset: 0,
                        isFocused: true,
                    })
                )
                    .collapseToStartOfBlock()
                    .focus();
            }
        }

        // If we should normalize, do it on the whole document, after everything.
        if (normalize) {
            this.normalizeDocument();
        }

        return this;
    }

    /**
     * Delete all the blocks in a BlockRange.
     */
    public deleteBlockRange(range: BlockRange, options: ChangeFlags = {}): Change {
        const normalize = this.getFlag('normalize', options);

        this.snapshotSelection();

        range.getBlocks(this.value.document).forEach((block) => {
            this.removeNodeByKey(block.key, { normalize: false });
        });

        // Normalize the entire document once we've deleted.
        if (normalize) {
            this.normalizeDocument();
        }

        return this;
    }

    /**
     * Delete backward until the character boundary at a `range`.
     */
    public deleteCharBackwardAtRange(range: TextRange, options: ChangeFlags = {}): Change {
        const { value } = this;
        const { document } = value;
        const { startKey, startOffset } = range as SetTextRange;
        if (!startKey) {
            return this;
        }
        const startBlock = document.getClosestBlock(startKey)!;
        if (!startBlock) {
            return this;
        }
        const offset = startBlock.getOffset(startKey);
        const o = offset + startOffset;
        const { text } = startBlock;
        const n = getCharOffsetBackward(text, o);
        this.deleteBackwardAtRange(range, n, options);
        return this;
    }

    /**
     * Delete backward until the line boundary at a `range`.
     */
    public deleteLineBackwardAtRange(range: TextRange, options: ChangeFlags = {}): Change {
        const { value } = this;
        const { document } = value;
        const { startKey, startOffset } = range as SetTextRange;
        const startBlock = document.getClosestBlock(startKey)!;
        const offset = startBlock.getOffset(startKey);
        const o = offset + startOffset;
        this.deleteBackwardAtRange(range, o, options);

        return this;
    }

    /**
     * Delete backward until the word boundary at a `range`.
     */
    public deleteWordBackwardAtRange(range: TextRange, options: ChangeFlags = {}): Change {
        const { value } = this;
        const { document } = value;
        const { startKey, startOffset } = range as SetTextRange;
        const startBlock = document.getClosestBlock(startKey)!;
        const offset = startBlock.getOffset(startKey);
        const o = offset + startOffset;
        const { text } = startBlock;
        const n = getWordOffsetBackward(text, o);
        this.deleteBackwardAtRange(range, n, options);

        return this;
    }

    /**
     * Delete backward `n` characters at a `range`.
     */
    public deleteBackwardAtRange(range: TextRange, n = 1, options: ChangeFlags = {}): Change {
        if (n === 0) {
            return this;
        }
        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;
        const { startKey, focusOffset } = range as SetTextRange;

        // If the range is expanded, perform a regular delete instead.
        if (range.isExpanded) {
            this.deleteAtRange(range, { normalize });
            return this;
        }

        const voidParent = document.getClosestVoid(startKey)!;

        // If there is a void parent, delete it.
        if (voidParent) {
            this.removeNodeByKey(voidParent.key, { normalize });
            return this;
        }

        const block = document.getClosestBlock(startKey)!;

        // If the closest is not void, but empty, remove it
        if (block?.isEmpty && document.nodes.size !== 1) {
            this.removeNodeByKey(block.key, { normalize });
            return this;
        }

        // If the range is at the start of the document, abort.
        const scope = document.getFragmentParent(startKey) ?? document;
        if (range.isAtStartOf(scope)) {
            return this;
        }

        // If the range is at the start of the text node, we need to figure out what
        // is behind it to know how to delete...
        const text = document.getDescendant(startKey)!;

        if (range.isAtStartOf(text)) {
            const prev = document.getPreviousText(text.key)!;
            if (!prev) {
                throw new Error(`Unable to find previous text node for "${text.key}"`);
            }

            const prevBlock = document.getClosestBlock(prev.key)!;
            const prevVoid = document.getClosestVoid(prev.key)!;

            // If the previous text node has a void parent, remove it.
            if (prevVoid) {
                this.removeNodeByKey(prevVoid.key, { normalize });
                return this;
            }

            // If we're deleting by one character and the previous text node is not
            // inside the current block, we need to merge the two blocks together.
            if (n === 1 && prevBlock !== block) {
                range = range.merge({
                    anchorKey: prev.key,
                    anchorOffset: prev.text.length,
                });

                this.deleteAtRange(range, { normalize });
                return this;
            }
        }

        // If the focus offset is farther than the number of characters to delete,
        // just remove the characters backwards inside the current node.
        if (n < focusOffset) {
            range = range.merge({
                focusOffset: focusOffset - n,
                isBackward: true,
            });

            this.deleteAtRange(range, { normalize });
            return this;
        }

        // Otherwise, we need to see how many nodes backwards to go.
        let node = text;
        let offset = 0;
        let traversed = focusOffset;

        while (n > traversed) {
            node = document.getPreviousText(node.key)!;
            const next = traversed + node.text.length;

            if (n <= next) {
                offset = next - n;
                break;
            }
            traversed = next;
        }

        range = range.merge({
            focusKey: node.key,
            focusOffset: offset,
            isBackward: true,
        });

        this.deleteAtRange(range, { normalize });

        return this;
    }

    /**
     * Delete forward until the character boundary at a `range`.
     */
    public deleteCharForwardAtRange(range: TextRange, options: ChangeFlags = {}): Change {
        const { value } = this;
        const { document } = value;
        const { startKey, startOffset } = range as SetTextRange;
        const startBlock = document.getClosestBlock(startKey)!;
        const offset = startBlock.getOffset(startKey);
        const o = offset + startOffset;
        const { text } = startBlock;
        const n = getCharOffsetForward(text, o);
        this.deleteForwardAtRange(range, n, options);

        return this;
    }

    /**
     * Delete forward until the line boundary at a `range`.
     */
    public deleteLineForwardAtRange(range: TextRange, options: ChangeFlags = {}): Change {
        const { value } = this;
        const { document } = value;
        const { startKey, startOffset } = range as SetTextRange;
        const startBlock = document.getClosestBlock(startKey)!;
        const offset = startBlock.getOffset(startKey);
        const o = offset + startOffset;
        this.deleteForwardAtRange(range, startBlock.text.length - o, options);

        return this;
    }

    /**
     * Delete forward until the word boundary at a `range`.
     */
    public deleteWordForwardAtRange(range: TextRange, options: ChangeFlags = {}): Change {
        const { value } = this;
        const { document } = value;
        const { startKey, startOffset } = range as SetTextRange;
        const startBlock = document.getClosestBlock(startKey)!;
        const offset = startBlock.getOffset(startKey);
        const o = offset + startOffset;
        const { text } = startBlock;
        const n = getWordOffsetForward(text, o);
        this.deleteForwardAtRange(range, n, options);

        return this;
    }

    /**
     * Delete forward `n` characters at a `range`.
     */
    public deleteForwardAtRange(range: TextRange, n = 1, options: ChangeFlags = {}): Change {
        if (n === 0) {
            return this;
        }
        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;
        const { startKey, focusOffset } = range as SetTextRange;

        // If the range is expanded, perform a regular delete instead.
        if (range.isExpanded) {
            this.deleteAtRange(range, { normalize });
            return this;
        }

        const voidParent = document.getClosestVoid(startKey)!;

        // If the node has a void parent, delete it.
        if (voidParent) {
            this.removeNodeByKey(voidParent.key, { normalize });
            return this;
        }

        const block = document.getClosestBlock(startKey)!;

        // If the closest is not void, but empty, remove it
        if (block?.isEmpty && document.nodes.size !== 1) {
            const nextBlock = document.getNextBlock(block.key);
            this.removeNodeByKey(block.key, { normalize });

            if (nextBlock?.key) {
                this.moveToStartOf(nextBlock);
            }
            return this;
        }

        // If the range is at the start of the document, abort.
        const scope = document.getFragmentParent(startKey) ?? document;
        if (range.isAtEndOf(scope)) {
            return this;
        }

        // If the range is at the start of the text node, we need to figure out what
        // is behind it to know how to delete...
        const text = document.getDescendant(startKey)!;

        if (range.isAtEndOf(text)) {
            const next = document.getNextText(text.key)!;
            const nextBlock = document.getClosestBlock(next.key)!;
            const nextVoid = document.getClosestVoid(next.key)!;

            // If the next text node has a void parent, remove it.
            if (nextVoid) {
                this.removeNodeByKey(nextVoid.key, { normalize });
                return this;
            }

            // If we're deleting by one character and the previous text node is not
            // inside the current block, we need to merge the two blocks together.
            if (n === 1 && nextBlock !== block) {
                range = range.merge({
                    focusKey: next.key,
                    focusOffset: 0,
                });

                this.deleteAtRange(range, { normalize });
                return this;
            }
        }

        // If the remaining characters to the end of the node is greater than or equal
        // to the number of characters to delete, just remove the characters forwards
        // inside the current node.
        if (n <= text.text.length - focusOffset) {
            range = range.merge({
                focusOffset: focusOffset + n,
            });

            this.deleteAtRange(range, { normalize });
            return this;
        }

        // Otherwise, we need to see how many nodes forwards to go.
        let node = text;
        let offset = focusOffset;
        let traversed = text.text.length - focusOffset;

        while (n > traversed) {
            node = document.getNextText(node.key)!;
            const next = traversed + node.text.length;

            if (n <= next) {
                offset = n - traversed;
                break;
            }
            traversed = next;
        }

        // If the focus node is inside a void, go up until right before it.
        if (document.hasVoidParent(node.key)) {
            const parent = document.getClosestVoid(node.key)!;
            node = document.getPreviousText(parent.key)!;
            offset = node.text.length;
        }

        range = range.merge({
            focusKey: node.key,
            focusOffset: offset,
        });

        this.deleteAtRange(range, { normalize });

        return this;
    }

    /**
     * Insert a `block` node to replace a block range.
     */
    public insertBlockAtBlockRange(
        range: BlockRange,
        block: Block,
        options: ChangeFlags = {}
    ): Change {
        const { focusBlock, anchorBlock } = range;
        const normalize = this.getFlag('normalize', options);

        if (!focusBlock || !anchorBlock) {
            return this;
        }

        const parent = this.value.document.getParent(focusBlock)!;
        assertIsDefined(parent);

        const focusIndex = parent.getChildIndex(focusBlock);
        const anchorIndex = parent.getChildIndex(anchorBlock);

        const index = Math.min(focusIndex, anchorIndex);

        this.deleteAtRange(range, { normalize: false });

        this.insertNodeByKey(parent.key, index, block, { normalize: false });

        if (normalize) {
            this.normalizeNodeByKey(parent.key);
        }

        return this;
    }

    /**
     * Insert a `block` node at `range`.
     */
    public insertBlockAtRange(
        range: SelectionRange,
        attrs: BlockCreateProps,
        options: ChangeFlags = {}
    ): Change {
        const block = Block.create(attrs);
        const normalize = this.getFlag('normalize', options);

        if (BlockRange.isBlockRange(range)) {
            return this.insertBlockAtBlockRange(range, block, options);
        }

        if (range.isExpanded) {
            this.deleteAtRange(range);
            range = range.collapseToStart();
        }

        const { value } = this;
        const { document } = value;
        let { startKey, startOffset } = range;
        const startBlock = document.getClosestBlock(startKey!)!;
        const startInline = document.getClosestInline(startKey!)!;
        const parent = document.getParent(startBlock.key)!;
        const index = parent.getChildIndex(startBlock.key);

        if (startBlock.isVoid) {
            const extra = range.isAtEndOf(startBlock) ? 1 : 0;
            this.insertNodeByKey(parent.key, index + extra, block, { normalize });
        } else if (startBlock.isEmpty) {
            this.insertNodeByKey(parent.key, index + 1, block, { normalize });
        } else if (range.isAtStartOf(startBlock)) {
            this.insertNodeByKey(parent.key, index, block, { normalize });
        } else if (range.isAtEndOf(startBlock)) {
            this.insertNodeByKey(parent.key, index + 1, block, { normalize });
        } else {
            if (startInline?.isVoid) {
                const atEnd = range.isAtEndOf(startInline);
                const siblingText = atEnd
                    ? document.getNextText(startKey!)
                    : document.getPreviousText(startKey!);

                const splitRange = atEnd
                    ? range.moveToStartOf(siblingText!)
                    : range.moveToEndOf(siblingText!);

                startKey = splitRange.startKey;
                startOffset = splitRange.startOffset;
            }

            this.splitDescendantsByKey(startBlock.key, startKey!, startOffset, {
                normalize: false,
            });

            this.insertNodeByKey(parent.key, index + 1, block, { normalize });
        }

        if (normalize) {
            this.normalizeNodeByKey(parent.key);
        }

        return this;
    }

    public insertDocumentAtBlockRange(
        fragment: Document | Fragment,
        options: ChangeFlags = {}
    ): Change {
        const normalize = this.getFlag('normalize', options);

        const { value } = this;
        const { selection, document } = value;

        if (!isBlockRangeSelection(selection)) {
            return this;
        }

        this.selectText(
            TextRange.fromBlockRange(
                selection.moveSelectionForward(document),
                document
            ).collapseToStart()
        );

        value.blocks.forEach((block) => {
            this.removeNodeByKey(block.key, { normalize: false });
        });

        // If the fragment is empty, there's nothing to do after deleting.
        if (!fragment.nodes.size) {
            return this;
        }

        // Regenerate the keys for all of the fragments nodes, so that they're
        // guaranteed not to collide with the existing keys in the document. Otherwise
        // they will be regenerated automatically and we won't have an easy way to
        // reference them.
        fragment = fragment.regenerateAllKeys();

        const blocks = selection.getBlocks(document);
        const firstBlock = blocks.first();
        if (!firstBlock) {
            return this;
        }
        const parent = document.getParent(firstBlock.key)!;
        if (!parent) {
            return this;
        }
        const index = parent.getChildIndex(firstBlock.key);

        fragment.nodes.reverse().forEach((node) => {
            this.insertNodeByKey(parent.key, index, node, { normalize: false });
        });

        // Normalize if requested.
        if (normalize) {
            this.normalizeNodeByKey(parent.key);
        }

        return this;
    }

    /**
     * Insert a document at a `range`.
     */
    public insertDocumentAtRange(
        range: TextRange,
        fragment: Document | Fragment,
        options: ChangeFlags = {}
    ): Change {
        const normalize = this.getFlag('normalize', options);

        // Helper to normalize when requested and if we did work (isDirty)
        // If range is expanded or we don't have a specific parent to normalize by, normalize
        // the entire document. Else normalize only the parent node.
        const normalizeIfNeeded = (args: {
            range: TextRange;
            parent?: AncestorNode | null;
        }): void => {
            const { parent = null, range } = args;

            if (this.isDirty && normalize) {
                if (range.isExpanded || !parent) {
                    this.normalize();
                } else {
                    this.normalizeNodeByKey(parent.key);
                }
            }
        };

        // If the range is expanded, delete it first.
        if (range.isExpanded) {
            this.deleteAtRange(range, { normalize: false });

            if (range.startKey && this.value.document.getDescendant(range.startKey)) {
                range = range.collapseToStart();
            } else if (range.endKey) {
                range = range.collapseTo(range.endKey, 0);
            }
        }

        // If the fragment is empty, there's nothing to do after deleting.
        if (!fragment.nodes.size) {
            normalizeIfNeeded({ range });
            return this;
        }

        // Regenerate the keys for all of the fragments nodes, so that they're
        // guaranteed not to collide with the existing keys in the document. Otherwise
        // they will be regenerated automatically and we won't have an easy way to
        // reference them.
        fragment = fragment.regenerateAllKeys();

        // Calculate a few things...
        const { startKey, startOffset } = range as SetTextRange;

        if (!startKey) {
            normalizeIfNeeded({ range });
            return this;
        }

        const { value } = this;
        let { document } = value;
        let startText = document.getDescendant(startKey)!;

        if (!startText) {
            normalizeIfNeeded({ range });
            return this;
        }

        let startBlock = document.getClosestBlock(startText.key)!;

        if (!startBlock) {
            normalizeIfNeeded({ range });
            return this;
        }

        let startChild = startBlock.getFurthestAncestor(startText.key)!;
        const isAtStart = range.isAtStartOf(startBlock);
        const parent = document.getParent(startBlock.key)!;

        if (!parent) {
            normalizeIfNeeded({ range });
            return this;
        }

        const index = parent.getChildIndex(startBlock.key);
        const blocks = fragment.getLeafBlocks();
        const firstChild = fragment.nodes.first();
        const lastChild = fragment.nodes.last();
        const firstBlock = blocks.first();
        const lastBlock = blocks.last();

        if (!firstBlock || !lastBlock) {
            normalizeIfNeeded({ range, parent });
            return this;
        }

        // If the starting block is empty, e.g an empty paragraph,
        // we can replace it with the content of the fragment
        // otherwise we end up with an empty block followed by the fragment.
        if (parent?.isDocument() && startBlock?.isEmpty && range.isAtStartOf(startBlock)) {
            this.replaceWithDocumentByKey(startBlock.key, fragment, options);
            return this;
        }

        // If the fragment only contains a void block, use `insertBlock` instead.
        if (firstBlock === lastBlock && firstBlock.isVoid) {
            this.insertBlockAtRange(range, firstBlock, options);
            return this;
        }

        // If the fragment starts or ends with single nested block, (e.g., table),
        // do not merge this fragment with existing blocks.
        if (
            firstChild &&
            lastChild &&
            (fragment.hasBlocks(firstChild.key) || fragment.hasBlocks(lastChild.key))
        ) {
            fragment.nodes.reverse().forEach((node) => {
                this.insertBlockAtRange(range, node, options);
            });
            return this;
        }

        // If the first and last block aren't the same, we need to insert all of the
        // nodes after the fragment's first block at the index.
        if (firstBlock !== lastBlock) {
            const lonelyParent = fragment.getFurthest(
                firstBlock.key,
                (p) => p.isBlock() && p.nodes.size === 1
            );
            const lonelyChild = lonelyParent || firstBlock;
            const startIndex = parent.getChildIndex(startBlock.key);
            fragment = fragment.removeDescendant(lonelyChild.key);

            fragment.nodes.forEach((node, i) => {
                const newIndex = startIndex + i + 1;
                this.insertNodeByKey(parent.key, newIndex, node, {
                    normalize: false,
                });
            });
        }

        // Check if we need to split the node.
        if (startOffset !== 0 && startKey && startChild) {
            this.splitDescendantsByKey(startChild.key, startKey, startOffset, {
                normalize: false,
            });
        }

        // Update our variables with the new value.
        document = this.value.document;
        startText = document.getDescendant(startKey)!;

        if (!startText) {
            logger.warning('insertDocumentAtRange: startText not found');
            normalizeIfNeeded({ range, parent });
            return this;
        }

        startBlock = document.getClosestBlock(startKey)!;

        if (!startBlock) {
            logger.warning('insertDocumentAtRange: startBlock not found');
            normalizeIfNeeded({ range, parent });
            return this;
        }

        startChild = startBlock.getFurthestAncestor(startText.key)!;

        // If the first and last block aren't the same, we need to move any of the
        // starting block's children after the split into the last block of the
        // fragment, which has already been inserted.
        if (firstBlock !== lastBlock && startChild) {
            const nextChild = isAtStart ? startChild : startBlock.getNextSibling(startChild.key);
            const nextNodes: List<Inline | Text> = nextChild
                ? (startBlock.nodes.skipUntil((n) => n.key === nextChild.key) as List<
                      Inline | Text
                  >)
                : List.create<Inline | Text>();
            const lastIndex = lastBlock.nodes.size;

            nextNodes.forEach((node, i) => {
                const newIndex = lastIndex + i;

                this.moveNodeByKey(node.key, lastBlock.key, newIndex, {
                    normalize: false,
                });
            });
        }

        // If the starting block is empty, we replace it entirely with the first block
        // of the fragment, since this leads to a more expected behavior for the user.
        if (startBlock.isEmpty) {
            this.removeNodeByKey(startBlock.key, { normalize: false });
            this.insertNodeByKey(parent.key, index, firstBlock, {
                normalize: false,
            });

            /** START MVR-1291 HACK */
            // RTC cannot handle a single merge_node, which the above code would trigger.
            // Insert an empty Text node into the pasted content, which will cause normalize
            // to call two "merge adjacent text node" normalizations rather than one, thus generating
            // two merge_nodes and ensuring realtime doesn't break.
            // This can be removed when RTC handles single merge_node events
            this.insertNodeByKey(firstBlock.key, 0, Text.create(''), {
                normalize: false,
            });
            /** END MVR-1291 HACK */
        } else {
            // Otherwise, we maintain the starting block, and insert all of the first
            // block's inline nodes into it at the split point.
            const inlineChild = startBlock.getFurthestAncestor(startText.key)!;

            if (inlineChild) {
                const inlineIndex = startBlock.getChildIndex(inlineChild.key);

                firstBlock.nodes.forEach((inline, i) => {
                    const o = startOffset === 0 ? 0 : 1;
                    const newIndex = inlineIndex + i + o;

                    this.insertNodeByKey(startBlock.key, newIndex, inline, {
                        normalize: false,
                    });
                });
            }
        }

        // Normalize if requested.
        normalizeIfNeeded({ range, parent });

        return this;
    }

    /**
     * Insert an `inline` node at `range`.
     */
    public insertInlineAtRange(
        range: TextRange,
        attrs: InlineCreateProps,
        options: ChangeFlags = {}
    ): Change {
        const normalize = this.getFlag('normalize', options);
        const inline = Inline.create(attrs);

        if (range.isExpanded) {
            this.deleteAtRange(range, { normalize: false });
            range = range.collapseToStart();
        }

        const { value } = this;
        const { document } = value;
        const { startKey, startOffset } = range as SetTextRange;

        if (!startKey) {
            return this;
        }

        const parent = document.getParent(startKey)!;
        const startText = document.assertDescendant(startKey);

        if (!parent || parent.isVoid) {
            return this;
        }

        const index = parent.getChildIndex(startText.key);
        this.splitNodeByKey(startKey, startOffset, { normalize: false });
        this.insertNodeByKey(parent.key, index + 1, inline, { normalize: false });

        if (normalize) {
            this.normalizeNodeByKey(parent.key);
        }

        return this;
    }

    /**
     * Insert `text` at a `range`, with optional `marks`.
     */
    public insertTextAtRange(
        range: TextRange,
        text: string,
        marks,
        options: ChangeFlags = {}
    ): Change {
        let normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;
        const { startKey, startOffset } = range as SetTextRange;
        let key = startKey;
        let offset = startOffset;
        const parent = document.getParent(startKey)!;

        if (parent.isVoid) {
            return this;
        }

        if (range.isExpanded) {
            this.deleteAtRange(range, { normalize: false });

            // Update range start after delete
            if (this.value.startKey !== key) {
                key = this.value.startKey!;
                offset = this.value.startOffset;
            }
        }

        // PERF: Unless specified, don't normalize if only inserting text.
        if (normalize === undefined) {
            normalize = range.isExpanded && marks && marks.size !== 0;
        }

        this.insertTextByKey(key, offset, text, marks, { normalize: false });

        if (normalize) {
            // normalize in the narrowest existing block that originally contains startKey and endKey
            const commonAncestor = document.getCommonAncestor(startKey, range.endKey!)!;
            const ancestors = document
                .getAncestors(commonAncestor.key)!
                .push(commonAncestor as AncestorNode);
            const normalizeAncestor = ancestors.findLast((n) =>
                this.value.document.getDescendant(n.key)
            );
            // it is possible that normalizeAncestor doesn't return any node
            // on that case fallback to startKey to be normalized
            const normalizeKey = normalizeAncestor ? normalizeAncestor.key : key;
            this.normalizeNodeByKey(normalizeKey);
        }

        return this;
    }

    /**
     * Remove an existing `mark` to the characters at `range`.
     */
    public removeMarkAtRange(
        range: TextRange,
        mark,
        options: ChangeFlags = {},
        markOptions: MarkOptions = {}
    ): Change {
        if (range.isCollapsed) {
            return this;
        }

        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;
        const { focusKey, startKey, startOffset, endKey, endOffset } = range as SetTextRange;

        const fragment = focusKey ? document.getFragmentParent(focusKey) : null;
        const texts = fragment ? fragment.getTextsAtRange(range) : document.getTextsAtRange(range);
        texts.forEach((node) => {
            const { key } = node;
            let index = 0;
            let { length } = node.text;

            if (key === startKey) {
                index = startOffset;
            }
            if (key === endKey) {
                length = endOffset;
            }
            if (key === startKey && key === endKey) {
                length = endOffset - startOffset;
            }

            this.removeMarkByKey(
                key,
                index,
                length,
                mark,
                {
                    normalize,
                },
                markOptions
            );
        });

        return this;
    }

    /**
     * Set the `properties` of block nodes in a `range`.
     */
    public setBlocksAtRange(
        range: TextRange,
        properties: BlockCreateProps | InlineCreateProps = {},
        options: ChangeFlags = {}
    ): Change {
        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;
        const blocks = document.getBlocksAtRange(range);

        const { startKey, startOffset, endKey, endOffset, isCollapsed } = range as SetTextRange;
        const isStartVoid = document.hasVoidParent(startKey);
        const startBlock = document.getClosestBlock(startKey)!;
        const endBlock = document.getClosestBlock(endKey)!;

        // Check if we have a "hanging" selection case where the even though the
        // selection extends into the start of the end node, we actually want to
        // ignore that for UX reasons.
        const isHanging =
            isCollapsed === false &&
            startOffset === 0 &&
            endOffset === 0 &&
            isStartVoid === false &&
            startKey === startBlock.getFirstText()!.key &&
            endKey === endBlock.getFirstText()!.key;

        // If it's a hanging selection, ignore the last block.
        const sets = isHanging ? blocks.slice(0, -1) : blocks;

        sets.forEach((block) => {
            this.setNodeByKey(block.key, properties, { normalize });
        });

        return this;
    }

    /**
     * Set the `properties` of inline nodes in a `range`.
     */
    public setInlinesAtRange(
        range: TextRange,
        properties: BlockCreateProps | InlineCreateProps = {},
        options: ChangeFlags = {}
    ): Change {
        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;
        const inlines = document.getInlinesAtRange(range);

        inlines.forEach((inline) => {
            this.setNodeByKey(inline.key, properties, { normalize });
        });

        return this;
    }

    /**
     * Split the block nodes at a `range`, to optional `height`.
     */
    public splitBlockAtRange(range: TextRange, height = 1, options: ChangeFlags = {}): Change {
        const normalize = this.getFlag('normalize', options);

        const { startKey, startOffset, endOffset, endKey } = range as SetTextRange;
        const { value } = this;
        const { document } = value;
        let node = document.assertDescendant(startKey);
        let parent = document.getClosestBlock(node.key)!;
        let h = 0;

        while (parent && parent.object === 'block' && h < height) {
            node = parent;
            parent = document.getClosestBlock(parent.key)!;
            h++;
        }

        this.splitDescendantsByKey(node.key, startKey, startOffset, {
            normalize: normalize && range.isCollapsed,
        });

        if (range.isExpanded) {
            if (range.isBackward) {
                range = range.flip();
            }
            const nextBlock = this.value.document.getNextBlock(node.key);
            range = range.moveAnchorToStartOf(nextBlock!);

            if (startKey === endKey) {
                range = range.moveFocusTo(range.anchorKey!, endOffset - startOffset);
            }

            this.deleteAtRange(range, { normalize });
        }

        return this;
    }

    /**
     * Split the inline nodes at a `range`, to optional `height`.
     */
    public splitInlineAtRange(
        range: TextRange,
        height = Number.POSITIVE_INFINITY,
        options: ChangeFlags = {}
    ): Change {
        const normalize = this.getFlag('normalize', options);

        if (range.isExpanded) {
            this.deleteAtRange(range, { normalize });
            range = range.collapseToStart();
        }

        const { startKey, startOffset } = range as SetTextRange;
        const { value } = this;
        const { document } = value;
        let node = document.assertDescendant(startKey);
        let parent = document.getClosestInline(node.key)!;
        let h = 0;

        while (parent && parent.object === 'inline' && h < height) {
            node = parent;
            parent = document.getClosestInline(parent.key)!;
            h++;
        }

        this.splitDescendantsByKey(node.key, startKey, startOffset, {
            normalize,
        });

        return this;
    }

    /**
     * Add or remove a `mark` from the characters at `range`, depending on whether
     * it's already there.
     */
    public toggleMarkAtRange(range: TextRange, mark, options: ChangeFlags = {}): Change {
        if (range.isCollapsed) {
            return this;
        }

        mark = Mark.create(mark);

        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;
        const marks = document.getActiveMarksAtRange(range);
        const exists = marks.some((m) => m.equals(mark));

        if (exists) {
            this.removeMarkAtRange(range, mark, { normalize });
        } else {
            this.addMarkAtRange(range, mark, { normalize });
        }

        return this;
    }

    /**
     * Unwrap all of the block nodes in a `range` from a block with `properties`.
     */
    public unwrapBlockAtRange(
        range: TextRange,
        properties: BlockCreateProps | InlineCreateProps = {},
        options: ChangeFlags = {}
    ): Change {
        properties = createNodeProperties(properties);

        const normalize = this.getFlag('normalize', options);
        const blocks = this.value.document.getBlocksAtRange(range);
        const wrappers = blocks
            .map((block) => {
                const closest = this.value.document.getClosest(block.key, (parent) => {
                    if (parent.object !== 'block') {
                        return false;
                    }
                    if (properties.type != null && parent.type !== properties.type) {
                        return false;
                    }
                    if (properties.isVoid != null && parent.isVoid !== properties.isVoid) {
                        return false;
                    }
                    if (properties.data != null && !parent.data.isSuperset(properties.data)) {
                        return false;
                    }
                    return true;
                });

                if (closest) {
                    assert(Block.isBlock(closest), 'unwrapBlockAtRange: closest is not a block');
                }

                return closest;
            })
            .filter(filterOutNullable)
            .unique();

        wrappers.forEach((block) => {
            assert(block, 'unwrapBlockAtRange: block is undefined, but should have been filtered');
            const first = block.nodes.first();
            const last = block.nodes.last();
            const parent = this.value.document.getParent(block.key)!;
            const index = parent.getChildIndex(block.key);

            const children = block.nodes.filter((child) => {
                return blocks.some((b) => child === b || child.hasDescendant(b.key));
            });

            const firstMatch = children.first();
            const lastMatch = children.last();

            if (first === firstMatch && last === lastMatch) {
                block.nodes.forEach((child, i) => {
                    this.moveNodeByKey(child.key, parent.key, index + i, {
                        normalize: false,
                    });
                });

                this.removeNodeByKey(block.key, { normalize: false });
            } else if (last === lastMatch) {
                block.nodes
                    .skipUntil((n) => n === firstMatch)
                    .forEach((child, i) => {
                        this.moveNodeByKey(child.key, parent.key, index + 1 + i, {
                            normalize: false,
                        });
                    });
            } else if (first === firstMatch) {
                (block.nodes as List<ChildNode>)
                    .takeUntil((n) => n === lastMatch)
                    .push(lastMatch as ChildNode)
                    .forEach((child, i) => {
                        this.moveNodeByKey(child.key, parent.key, index + i, {
                            normalize: false,
                        });
                    });
            } else {
                const firstText = (firstMatch as AncestorNode).getFirstText()!;

                this.splitDescendantsByKey(block.key, firstText.key, 0, {
                    normalize: false,
                });

                children.forEach((child, i) => {
                    if (i === 0) {
                        const extra = child;
                        child = this.value.document.getNextBlock(child.key);
                        this.removeNodeByKey(extra.key, { normalize: false });
                    }

                    this.moveNodeByKey(child.key, parent.key, index + 1 + i, {
                        normalize: false,
                    });
                });
            }
        });

        if (normalize) {
            this.normalizeDocument();
        }

        return this;
    }

    /**
     * Unwrap the inline nodes in a `range` from an inline with `properties`.
     */
    public unwrapInlineAtRange(
        range: TextRange,
        properties: BlockCreateProps | InlineCreateProps = {},
        options: ChangeFlags = {}
    ): Change {
        properties = createNodeProperties(properties);

        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;
        const texts = document.getTextsAtRange(range);

        const inlines = texts
            .map((text) => {
                const closest = document.getClosest(text.key, (parent) => {
                    if (parent.object !== 'inline') {
                        return false;
                    }
                    if (properties.type != null && parent.type !== properties.type) {
                        return false;
                    }
                    if (properties.isVoid != null && parent.isVoid !== properties.isVoid) {
                        return false;
                    }
                    if (properties.data != null && !parent.data.isSuperset(properties.data)) {
                        return false;
                    }
                    return true;
                });

                if (closest) {
                    assert(
                        Inline.isInline(closest),
                        'unwrapInlineAtRange: closest is not a inline'
                    );
                }

                return closest;
            })
            .filter(filterOutNullable)
            .unique();

        inlines.forEach((inline) => {
            assert(
                inline,
                'unwrapInlineAtRange: inline is undefined, but should have been filtered'
            );

            const parent = this.value.document.getParent(inline.key)!;
            const index = parent.getChildIndex(inline.key);

            inline.nodes.forEach((child, i) => {
                this.moveNodeByKey(child.key, parent.key, index + i, {
                    normalize: false,
                });
            });

            // Remove the now empty inline node.
            this.removeNodeByKey(inline.key, { normalize: false });
        });

        if (normalize) {
            this.normalizeDocument();
        }

        return this;
    }

    /**
     * Wrap all of the blocks in a `range` in a new `block`.
     */
    public wrapBlockAtRange(
        range: TextRange,
        attrs: BlockCreateProps,
        options: ChangeFlags = {}
    ): Change {
        let block = Block.create(attrs);
        block = block.set('nodes', block.nodes.clear());

        const normalize = this.getFlag('normalize', options);
        const { value } = this;
        const { document } = value;

        const blocks = document.getBlocksAtRange(range);
        const firstblock = blocks.first()!;
        const lastblock = blocks.last()!;
        let parent;
        let siblings;
        let index;

        // If there is only one block in the selection then we know the parent and
        // siblings.
        if (blocks.size === 1) {
            parent = document.getParent(firstblock.key);
            siblings = blocks;
        } else {
            // Determine closest shared parent to all blocks in selection.
            parent = document.getClosest(firstblock.key, (p1) => {
                return Boolean(document.getClosest(lastblock.key, (p2) => p1 === p2));
            });
        }

        // If no shared parent could be found then the parent is the document.
        if (parent == null) {
            parent = document;
        }

        // Create a list of direct children siblings of parent that fall in the
        // selection.
        if (siblings == null) {
            const indexes = parent.nodes.reduce((ind, node, i) => {
                if (node === firstblock || node.hasDescendant(firstblock.key)) {
                    ind[0] = i;
                }
                if (node === lastblock || node.hasDescendant(lastblock.key)) {
                    ind[1] = i;
                }
                return ind;
            }, []);

            index = indexes[0];
            siblings = parent.nodes.slice(indexes[0], indexes[1] + 1);
        }

        // Get the index to place the new wrapped node at.
        if (index == null) {
            index = parent.nodes.indexOf(siblings.first());
        }

        // Inject the new block node into the parent.
        this.insertNodeByKey(parent.key, index, block, { normalize: false });

        // Move the sibling nodes into the new block node.
        siblings.forEach((node, i) => {
            this.moveNodeByKey(node.key, block.key, i, { normalize: false });
        });

        if (normalize) {
            this.normalizeNodeByKey(parent.key);
        }

        return this;
    }

    /**
     * Wrap the text and inlines in a `range` in a new `inline`.
     */
    public wrapInlineAtRange(
        range: TextRange,
        attrs: InlineCreateProps,
        options: ChangeFlags = {}
    ): Change {
        const { value } = this;
        let { document } = value;
        const normalize = this.getFlag('normalize', options);
        const { startKey, startOffset, endKey, endOffset } = range as SetTextRange;

        if (!startKey || !endKey) {
            return this;
        }

        if (range.isCollapsed) {
            // Wrapping an inline void
            const inlineParent = document.getClosestInline(startKey)!;

            if (!inlineParent || !inlineParent.isVoid) {
                return this;
            }

            return this.wrapInlineByKey(inlineParent.key, attrs, options);
        }

        let inline = Inline.create(attrs);
        inline = inline.set('nodes', inline.nodes.clear());

        const blocks = document.getBlocksAtRange(range);
        let startBlock = document.getClosestBlock(startKey)!;
        let endBlock = document.getClosestBlock(endKey)!;
        const startInline = document.getClosestInline(startKey)!;
        const endInline = document.getClosestInline(endKey)!;
        let startChild = startBlock.getFurthestAncestor(startKey)!;
        let endChild = endBlock.getFurthestAncestor(endKey)!;

        if (!startInline || startInline !== endInline) {
            this.splitDescendantsByKey(endChild.key, endKey, endOffset, {
                normalize: false,
            });

            this.splitDescendantsByKey(startChild.key, startKey, startOffset, {
                normalize: false,
            });
        }

        document = this.value.document;
        startBlock = document.getDescendant(startBlock.key)!;
        endBlock = document.getDescendant(endBlock.key)!;
        startChild = startBlock.getFurthestAncestor(startKey)!;
        endChild = endBlock.getFurthestAncestor(endKey)!;
        const startIndex = startBlock.getChildIndex(startChild.key);
        const endIndex = endBlock.getChildIndex(endChild.key);

        if (startInline && startInline === endInline) {
            const text = startBlock
                .getTextsAtRange(range)
                .get(0)!
                .splitText(startOffset)[1]
                .splitText(endOffset - startOffset)[0];
            inline = inline.set('nodes', List.create([text]));
            this.insertInlineAtRange(range, inline, {
                normalize: false,
            });
            const inlinekey = inline.getFirstText()!.key;
            const rng = {
                anchorKey: inlinekey,
                focusKey: inlinekey,
                anchorOffset: 0,
                focusOffset: endOffset - startOffset,
                isFocused: true,
            };
            this.select(rng);
        } else if (startBlock === endBlock) {
            document = this.value.document;
            startBlock = document.getClosestBlock(startKey)!;
            startChild = startBlock.getFurthestAncestor(startKey)!;

            const startInner = document.getNextSibling(startChild.key)!;
            const startInnerIndex = startBlock.getChildIndex(startInner.key);
            const endInner =
                startKey === endKey ? startInner : startBlock.getFurthestAncestor(endKey);
            const inlines = (startBlock.nodes as List<ChildNode>)
                .skipUntil((n) => n === startInner)
                .takeUntil((n) => n === endInner)
                .push(endInner as ChildNode);

            const node = inline.regenerateKey();

            this.insertNodeByKey(startBlock.key, startInnerIndex, node, {
                normalize: false,
            });

            inlines.forEach((child, i) => {
                this.moveNodeByKey(child.key, node.key, i, { normalize: false });
            });

            if (normalize) {
                this.normalizeNodeByKey(startBlock.key);
            }
        } else {
            const startInlines = startBlock.nodes.slice(startIndex + 1);
            const endInlines = endBlock.nodes.slice(0, endIndex + 1);
            const startNode = inline.regenerateKey();
            const endNode = inline.regenerateKey();

            this.insertNodeByKey(startBlock.key, startIndex + 1, startNode, {
                normalize: false,
            });

            this.insertNodeByKey(endBlock.key, endIndex, endNode, {
                normalize: false,
            });

            startInlines.forEach((child, i) => {
                this.moveNodeByKey(child.key, startNode.key, i, {
                    normalize: false,
                });
            });

            endInlines.forEach((child, i) => {
                this.moveNodeByKey(child.key, endNode.key, i, {
                    normalize: false,
                });
            });

            if (normalize) {
                this.normalizeNodeByKey(startBlock.key).normalizeNodeByKey(endBlock.key);
            }

            blocks.slice(1, -1).forEach((block) => {
                const node = inline.regenerateKey();
                this.insertNodeByKey(block.key, 0, node, { normalize: false });

                block.nodes.forEach((child, i) => {
                    this.moveNodeByKey(child.key, node.key, i, {
                        normalize: false,
                    });
                });

                if (normalize) {
                    this.normalizeNodeByKey(block.key);
                }
            });
        }

        return this;
    }

    /**
     * Wrap the text in a `range` in a prefix/suffix.
     */
    public wrapTextAtRange(
        range: TextRange,
        prefix: string,
        suffix = prefix,
        options: ChangeFlags = {}
    ): Change {
        const normalize = this.getFlag('normalize', options);
        const { startKey, endKey } = range as SetTextRange;
        const start = range.collapseToStart();
        let end = range.collapseToEnd();

        if (startKey === endKey) {
            end = end.move(prefix.length);
        }

        this.insertTextAtRange(start, prefix, [], { normalize });
        this.insertTextAtRange(end, suffix, [], { normalize });

        return this;
    }

    /**
     * Add a `mark` to the characters in the current selection.
     */
    public addMark(markProps: MarkCreateAttrs, options: ChangeFlags = {}): Change {
        const mark = Mark.create(markProps);
        const { value } = this;
        const { document } = value;
        const selection = value.selection as TextRange;

        if (selection.isExpanded) {
            this.addMarkAtRange(selection, mark, options);
        } else if (selection.marks) {
            const marks = selection.marks.add(mark);
            const sel = selection.set('marks', marks);
            this.select(sel);
        } else {
            const marks = document.getActiveMarksAtRange(selection).add(mark);
            const sel = selection.set('marks', marks);
            this.select(sel);
        }

        return this;
    }

    /**
     * Add a list of `marks` to the characters in the current selection.
     */
    public addMarks(marks: Array<MarkCreateAttrs>): Change {
        marks.forEach((mark) => this.addMark(mark));

        return this;
    }

    /**
     * Delete at the current selection.
     */
    public delete(options: ChangeFlags = {}): Change {
        const { value } = this;
        const { selection } = value;
        this.deleteAtRange(selection, options);

        // Ensure that the selection is collapsed to the start, because in certain
        // cases when deleting across inline nodes, when splitting the inline node the
        // end point of the selection will end up after the split point.
        this.collapseToStart();

        return this;
    }

    /**
     * Insert or replace the current block.
     */
    public insertOrReplaceBlock(block: Block): Change {
        const { selection, startBlock } = this.value;

        if (startBlock?.isEmpty) {
            this.replaceNodeByKey(startBlock.key, block);
        } else {
            this.insertBlock(block);
        }

        const inserted = this.value.document.getByKey(block.key);
        if (!inserted || inserted.object !== 'block') {
            return this;
        }

        if (BlockRange.isBlockRange(selection)) {
            this.selectBlock(BlockRange.createAtBlock(inserted));
        } else {
            this.collapseToStartOf(inserted);
        }

        return this;
    }

    /**
     * Insert a `block` at the current selection.
     */
    public insertBlock(input: Block | BlockCreateProps, options: ChangeFlags = {}): Change {
        const block = Block.create(input);
        const { value } = this;
        const { selection } = value;
        this.insertBlockAtRange(selection, block, options);

        // If the node was successfully inserted, update the selection.
        const node = this.value.document.getNode(block.key)!;
        if (node && node.object === 'block') {
            if (BlockRange.isBlockRange(selection)) {
                this.selectBlock(BlockRange.createAtBlock(node));
            } else {
                this.collapseToEndOf(node);
            }
        }

        return this;
    }

    /**
     * Insert content of another document or fragment at the current selection.
     */
    public insertDocument(fragment: Document | Fragment, options: ChangeFlags = {}): Change {
        if (!fragment.nodes.size) {
            return this;
        }

        let { value } = this;
        let { document, selection } = value;

        if (isBlockRangeSelection(selection)) {
            const blockKeys = document.getBlocks().map((block) => block.key);
            this.insertDocumentAtBlockRange(fragment, options);

            value = this.value;
            document = value.document;
            selection = value.selection;

            const firstBlock = document.getBlocks().find((n) => !blockKeys.includes(n.key));
            const lastBlock = document.getBlocks().findLast((n) => !blockKeys.includes(n.key));
            if (firstBlock && lastBlock) {
                this.select(
                    BlockRange.create({ anchorBlock: firstBlock.key, focusBlock: lastBlock.key })
                );
            }
            return this;
        }

        const { startText, endText, startInline } = value;
        const lastText = fragment.getLastText()!;
        const lastInline = fragment.getClosestInline(lastText.key)!;
        const firstChild = fragment.nodes.first()!;
        const lastChild = fragment.nodes.last()!;
        const keys = document.getTexts().map((text) => text.key);
        const isAppending =
            !startInline ||
            selection.hasEdgeAtStartOf(startText!) ||
            selection.hasEdgeAtEndOf(endText!);

        const isInserting = fragment.hasBlocks(firstChild.key) || fragment.hasBlocks(lastChild.key);

        this.insertDocumentAtRange(selection, fragment, options);
        value = this.value;
        document = value.document;

        const newTexts = document.getTexts().filter((n) => !keys.includes(n.key));
        const newText = isAppending ? newTexts.last() : newTexts.takeLast(2).first();

        if (newText && (lastInline || isInserting)) {
            this.select(selection.collapseToEndOf(newText));
        } else if (newText) {
            this.select(selection.collapseToStartOf(newText).move(lastText.text.length));
        } else {
            // No new text could be found because:
            // A. We prolonged the current text sometimes => advance to by the inserted text length
            // B. Normalization removed the inserted text => try to advance, and select's normalization will avoid off-limit offsets.
            this.select(selection.collapseToStart().move(lastText.text.length));
        }

        return this;
    }

    /**
     * Insert an `inline` at the current selection.
     */
    public insertInline(inline: Inline | InlineCreateProps, options: ChangeFlags = {}): Change {
        inline = Inline.create(inline);
        const { value } = this;
        const selection = value.selection as TextRange;
        this.insertInlineAtRange(selection, inline, options);

        // If the node was successfully inserted, update the selection.
        const node = this.value.document.getNode(inline.key!)!;
        if (node) {
            this.collapseToEndOf(node);
        }

        return this;
    }

    /**
     * Insert a string of `text` with optional `marks` at the current selection.
     */
    public insertText(
        text: string,
        marks?: Array<MarkCreateAttrs>,
        options: ChangeFlags = {}
    ): Change {
        const { value } = this;
        const { document } = value;
        const selection = value.selection as TextRange;
        const resolvedMarks = marks || selection.marks || document.getInsertMarksAtRange(selection);
        this.insertTextAtRange(selection, text, resolvedMarks, options);

        // If the text was successfully inserted, and the selection had marks on it,
        // unset the selection's marks.
        if (selection.marks && document !== this.value.document) {
            this.select({ marks: null });
        }

        return this;
    }

    /**
     * Split the block node at the current selection, to optional `depth`.
     */
    public splitBlock(depth = 1, options: ChangeFlags = {}): Change {
        const { value } = this;
        const { document } = value;
        const selection = value.selection as TextRange;
        const marks = selection.marks || document.getInsertMarksAtRange(selection);
        this.splitBlockAtRange(selection, depth, options).collapseToEnd();

        if (marks && marks.size !== 0) {
            this.select({ marks: marks as unknown as MarkCreateAttrs[] });
        }

        return this;
    }

    /**
     * Remove all marks.
     */
    public removeAllMarks(): Change {
        const { value } = this;

        // Use `marks` (union across the selection) rather than `activeMarks`
        // (intersection) so mixed-format selections are fully cleared.
        value.marks.forEach((mark) => {
            this.removeMark(mark);
        });

        return this;
    }

    /**
     * Remove a `mark` from the characters in the current selection.
     */
    public removeMark(
        mark: MarkCreateAttrs,
        options: ChangeFlags = {},
        markOptions: MarkOptions = {}
    ): Change {
        const tmpMark = Mark.create(mark);
        const { value } = this;
        const { document } = value;
        const selection = value.selection as TextRange;

        if (!selection.isExpanded && markOptions.property) {
            const marks = selection.marks || document.getActiveMarksAtRange(selection);
            const existingMark = marks.find((mark) => mark.type === tmpMark.type);
            if (!existingMark || !existingMark.data.has(markOptions.property)) {
                return this;
            }

            const updatedData = existingMark.data.set(markOptions.property, 'default');
            const updatedMark = existingMark.set('data', updatedData);
            let nextMarks = marks.delete(existingMark);

            if (!updatedData.every((value) => value === 'default')) {
                nextMarks = nextMarks.add(updatedMark);
            }

            this.select(selection.set('marks', nextMarks));
            return this;
        }

        if (selection.isExpanded) {
            this.removeMarkAtRange(selection, tmpMark, options, markOptions);
        } else if (selection.marks) {
            const marks = selection.marks.delete(tmpMark);
            const sel = selection.set('marks', marks);
            this.select(sel);
        } else {
            const marks = document.getActiveMarksAtRange(selection).delete(tmpMark);
            const sel = selection.set('marks', marks);
            this.select(sel);
        }

        return this;
    }

    /**
     * Replace an `oldMark` with a `newMark` in the characters in the current selection.
     */
    public replaceMark(
        oldMark: MarkCreateAttrs,
        newMark: MarkCreateAttrs,
        options: ChangeFlags = {}
    ): Change {
        this.removeMark(oldMark, options);
        this.addMark(newMark, options);

        return this;
    }

    /**
     * Add or remove a `mark` from the characters in the current selection,
     * depending on whether it's already there.
     */
    public toggleMark(mark: MarkCreateAttrs, options: ChangeFlags = {}): Change {
        const tmpMark = Mark.create(mark);
        const { value } = this;
        const exists = value.activeMarks.has(tmpMark);

        if (exists) {
            this.removeMark(tmpMark, options);
        } else {
            this.addMark(tmpMark, options);
        }

        return this;
    }

    /**
     * Wrap the current selection with prefix/suffix.
     */
    public wrapText(prefix: string, suffix: string = prefix, options: ChangeFlags = {}): Change {
        const { value } = this;
        const selection = value.selection as TextRange;
        this.wrapTextAtRange(selection, prefix, suffix, options);

        // If the selection was collapsed, it will have moved the start offset too.
        if (selection.isCollapsed) {
            this.moveStart(-1 * prefix.length);
        }

        // Adding the suffix will have pushed the end of the selection further on, so
        // we need to move it back to account for this.
        this.moveEnd(-1 * suffix.length);

        // There's a chance that the selection points moved "through" each other,
        // resulting in a now-incorrect selection direction.
        if (selection.isForward !== (this.value.selection as TextRange).isForward) {
            this.flip();
        }

        return this;
    }

    /**
     * Duplicate blocks specified by `blockKeys` in the node specified by `key`.
     */
    public duplicateBlocksByKey(key: string, blockKeys: string | string[]): Change {
        blockKeys = Array.isArray(blockKeys) ? blockKeys : [blockKeys];

        const node = this.value.document.getNode(key)!;

        if (!node) {
            return this;
        }

        if (node.object === 'inline' || node.object === 'text') {
            return this;
        }

        const blocks = blockKeys.map((key) => {
            const block = node.getNode(key)!;
            assert(block && block.object === 'block');
            return block;
        });

        const lastBlock = blocks[blocks.length - 1];

        // No-block, nothing to do
        if (!lastBlock) {
            return this;
        }

        const lastBlockIndex = node.getChildIndex(lastBlock.key);
        const sortedBlocks = Array.from(blocks).sort(
            (a, b) => node.getChildIndex(b.key) - node.getChildIndex(a.key)
        );

        // Insert blocks after the last selected block, in the order they were selected
        const newBlocks = sortedBlocks.map((block) => {
            const newBlock = block.regenerateAllKeys();
            this.insertNodeByKey(node.key, lastBlockIndex + 1, newBlock);
            return newBlock;
        });

        const first = newBlocks[0];
        const last = newBlocks[newBlocks.length - 1];
        assert(first && last);

        return this.selectBlock(
            BlockRange.create({
                anchorBlock: first.key,
                focusBlock: last.key,
            })
        ).focus();
    }

    /**
     * Duplicate all blocks in the current selection.
     */
    public duplicateBlocks(): Change {
        const { blocks } = this.value;
        const firstBlock = blocks.first();
        if (!firstBlock) {
            return this;
        }
        const parent = this.value.document.getParent(firstBlock.key)!;
        assert(parent, 'duplicateBlocks: firstBlock has no parent');
        return this.duplicateBlocksByKey(parent.key, blocks.map((b) => b.key).toArray());
    }

    /**
     * Normalize the value with its schema.
     */
    public normalize(): Change {
        this.normalizeDocument();
        return this;
    }

    /**
     * Normalize the document with the value's schema.
     */
    public normalizeDocument(): Change {
        const { value } = this;
        const { document } = value;
        this.normalizeNodeByKey(document.key);

        return this;
    }

    /**
     * Normalize the selection.
     */
    public normalizeSelection(): Change {
        const { value } = this;
        const { selection, document } = value;

        if (selection.isSet) {
            this.select(selection.normalize(document));
        } else if (document.nodes.size > 0) {
            this.collapseToStartOf(document);

            if (selection.isFocused && this.value.selection.isSet) {
                this.focus();
            }
        }

        return this;
    }

    /**
     * Normalize a `node` and its children with the value's schema.
     */
    public normalizeNodeByKey(key: string): Change {
        // If we are already normalizing this node, we don't need to do it again
        // and we'll reprocess the whole node after its children instead.
        if (this.normalizations[key]) {
            this.normalizations[key].pending = true;
            return this;
        }
        this.normalizations[key] = { pending: false };

        const { value } = this;
        const { schema } = value;
        let { document } = value;
        const node = document.assertNode(key);

        this.normalizeNodeAndChildren(node, schema);

        document = this.value.document;
        const ancestors = document.getAncestors(key);
        if (!ancestors) {
            return this;
        }

        ancestors.forEach((ancestor) => {
            const updated = this.value.document.getDescendant(ancestor.key)!;
            if (updated) {
                this.normalizeNode(updated, schema);
            }
        });

        // If a descendant requested to normalize this node again, we do it now.
        const shouldBeNormalizedAgain = this.normalizations[key].pending === true;
        delete this.normalizations[key];
        if (shouldBeNormalizedAgain) {
            const updated = this.value.document.getNode(key)!;
            if (updated) {
                this.normalizeNodeByKey(updated.key);
            }
        }

        return this;
    }

    /**
     * Normalize a `node` and its children with a `schema`.
     */
    private normalizeNodeAndChildren(node: KeyedNode, schema: Schema) {
        if (node.object === 'text') {
            this.normalizeNode(node, schema);
            return;
        }

        let child = schema.getFirstInvalidNode(node, this.value.document);
        let path: Path | null = this.value.document.getPath(node.key);
        let childPath = child ? this.value.document.getPath(child.key) : null;

        while (node && child) {
            assert(path, 'normalizeNodeAndChildren: node has no path');
            assert(childPath, 'normalizeNodeAndChildren: child has no path');

            // Resume from the child's previous location in document order.
            // If the child mutates or moves during normalization, the old path
            // still tells us which part of the subtree was already processed.
            const resumePath = childPath;
            const scope = this.enterNormalizationScope(path, resumePath);

            try {
                this.normalizeNodeAndChildren(child, schema);
            } finally {
                if (scope) {
                    this.exitNormalizationScope(scope);
                }
            }

            node = this.value.document.refindNode(path, node.key)!;

            if (!node) {
                path = [];
                child = null;
            } else {
                path = this.value.document.refindPath(path, node.key);
                // Unsafe writes mean the forward resume cursor is no longer trustworthy.
                // Restart from the top of the current subtree before looking for the next
                // invalid descendant.
                const shouldRestartFromTop = scope?.hasUnsafeOperations === true;
                child =
                    shouldRestartFromTop ||
                    // If the previous child disappeared or moved outside the subtree,
                    // we can no longer resume safely and must restart the search
                    // from the beginning of the current node.
                    resumePath === null ||
                    path === null ||
                    !this.isPathPrefix(path, resumePath)
                        ? schema.getFirstInvalidNode(node, this.value.document)
                        : // Otherwise keep scanning forward from the last touched child
                          // instead of rescanning earlier siblings again.
                          this.findFirstInvalidNodeAtOrAfterPath(node, schema, path, resumePath);
                childPath = child ? this.value.document.getPath(child.key) : null;
            }
        }

        // Normalize the node itself if it still exists.
        if (node) {
            this.normalizeNode(node, schema);
        }
    }

    /**
     * Find the next invalid descendant in document order, resuming from a previous child path.
     */
    private findFirstInvalidNodeAtOrAfterPath(
        node: KeyedNode,
        schema: Schema,
        nodePath: Path | null,
        startPath: Path
    ): KeyedNode | null {
        if (node.object === 'text') {
            return null;
        }

        for (let index = 0; index < node.nodes.size; index++) {
            const child = node.nodes.get(index);
            const childPath = [...(nodePath ?? []), index];
            const invalid = this.findInvalidInChildAtOrAfterPath(
                child!,
                childPath,
                startPath,
                schema
            );
            if (invalid) {
                return invalid;
            }
        }

        if ('fragments' in node && node.fragments) {
            const fragments = node.fragments.toValuesArray();
            for (let index = 0; index < fragments.length; index++) {
                const child = fragments[index];
                const childPath = [...(nodePath ?? []), node.nodes.size + index];
                const invalid = this.findInvalidInChildAtOrAfterPath(
                    child,
                    childPath,
                    startPath,
                    schema
                );
                if (invalid) {
                    return invalid;
                }
            }
        }

        return null;
    }

    private findInvalidInChildAtOrAfterPath(
        child: KeyedNode,
        childPath: Path,
        startPath: Path,
        schema: Schema
    ): KeyedNode | null {
        // Siblings strictly before the resume cursor have already been checked
        // in this pass, so they can be skipped.
        if (isPathBefore(childPath, startPath) && !this.isPathPrefix(childPath, startPath)) {
            return null;
        }

        if (schema.validateNode(child, this.value.document)) {
            return child;
        }

        if (child.object === 'text') {
            return null;
        }

        // When the resume cursor points inside this child, keep following it
        // down the subtree. Otherwise the child becomes the next place to start.
        const descendantStartPath = this.isPathPrefix(childPath, startPath) ? startPath : childPath;
        return this.findFirstInvalidNodeAtOrAfterPath(
            child,
            schema,
            childPath,
            descendantStartPath
        );
    }

    private isPathPrefix(parentPath: Path, path: Path): boolean {
        if (parentPath.length > path.length) {
            return false;
        }

        for (let index = 0; index < parentPath.length; index++) {
            if (parentPath[index] !== path[index]) {
                return false;
            }
        }

        return true;
    }

    private enterNormalizationScope(rootPath: Path, resumePath: Path): NormalizationScope {
        const scope: NormalizationScope = {
            rootPath,
            resumePath,
            hasUnsafeOperations: false,
            currentNodePath: null,
        };

        this.normalizationScopes.push(scope);

        return scope;
    }

    private exitNormalizationScope(scope: NormalizationScope): void {
        const index = this.normalizationScopes.lastIndexOf(scope);
        if (index >= 0) {
            this.normalizationScopes.splice(index, 1);
        }
    }

    private setCurrentNormalizationNodePath(path: Path): Array<Path | null> {
        const previousPaths = this.normalizationScopes.map((scope) => scope.currentNodePath);

        this.normalizationScopes.forEach((scope) => {
            scope.currentNodePath = path;
        });

        return previousPaths;
    }

    private restoreCurrentNormalizationNodePath(previousPaths: Array<Path | null>): void {
        this.normalizationScopes.forEach((scope, index) => {
            scope.currentNodePath = previousPaths[index] ?? null;
        });
    }

    /**
     * Track an operation in the context of a normalization pass to see if it violates normalization rules.
     */
    private trackNormalizationOperation(operation: Operation): void {
        // We're not currently in scope of any normalization, so we don't need to track anything.
        if (this.normalizationScopes.length === 0) {
            return;
        }

        // See if this operation is unsafe for any of the normalization scopes we're currently in.
        for (const scope of this.normalizationScopes) {
            const isUnsafe = this.isOperationUnsafeForNormalizationScope(operation, scope);

            if (!isUnsafe) {
                continue;
            }

            scope.hasUnsafeOperations = true;
            const message =
                `Unsafe normalization operation "${operation.type}" touched an earlier part of the subtree. ` +
                'Rewrite the normalization so it only changes the current subtree forward.';

            if (process.env.NODE_ENV === 'production') {
                logger.error(message);
            } else {
                throw new Error(message);
            }
        }
    }

    private isOperationUnsafeForNormalizationScope(
        operation: Operation,
        scope: NormalizationScope
    ): boolean {
        switch (operation.type) {
            case 'set_selection':
                return false;
            case 'set_document':
                return true;
            case 'insert_fragment':
                return this.isUnsafePositionWithinNormalizationScope(operation.fragmentPath, scope);
            case 'set_fragment':
            case 'remove_fragment':
                return this.isUnsafePathWithinNormalizationScope(operation.fragmentPath, scope);
            case 'insert_node':
                return this.isUnsafePositionWithinNormalizationScope(operation.path, scope);
            case 'remove_node':
            case 'set_node':
            case 'split_node':
            case 'insert_text':
            case 'remove_text':
            case 'add_mark':
            case 'remove_mark':
            case 'set_data':
            case 'remove_data':
            case 'insert_array_data':
            case 'remove_array_data':
                return this.isUnsafePathWithinNormalizationScope(operation.path, scope);
            case 'merge_node': {
                const mergeTargetPath = this.getPreviousSiblingPath(operation.path);
                return (
                    this.isUnsafePathWithinNormalizationScope(operation.path, scope) ||
                    (mergeTargetPath
                        ? this.isUnsafePathWithinNormalizationScope(mergeTargetPath, scope)
                        : false)
                );
            }
            case 'move_node':
                return (
                    this.isUnsafePathWithinNormalizationScope(operation.path, scope) ||
                    this.isMoveOutsideNormalizationScope(
                        operation.path,
                        operation.newPath,
                        scope
                    ) ||
                    this.isUnsafePositionWithinNormalizationScope(operation.newPath, scope)
                );
            default:
                assertNever(operation.type);
        }
    }

    /**
     * Returns true if moving a node from `path` to `newPath` would move it outside of the normalization scope.
     */
    private isMoveOutsideNormalizationScope(
        path: Path,
        newPath: Path,
        scope: NormalizationScope
    ): boolean {
        // A normalizer can move the node it is currently fixing out of the
        // parent subtree. The parent pass will resume from the node's previous
        // location, so this does not invalidate the forward scan.
        if (scope.currentNodePath && isPathEqual(path, scope.currentNodePath)) {
            return false;
        }

        return (
            this.isPathPrefix(scope.rootPath, path) && !this.isPathPrefix(scope.rootPath, newPath)
        );
    }

    private getPreviousSiblingPath(path: Path): Path | null {
        if (path.length === 0) {
            return null;
        }

        const lastIndex = path[path.length - 1];
        if (lastIndex <= 0) {
            return null;
        }

        return [...path.slice(0, -1), lastIndex - 1];
    }

    /**
     * Returns true if the given path is unsafe to modify within the normalization scope, meaning that it is before the resume path but not a prefix of it.
     */
    private isUnsafePathWithinNormalizationScope(path: Path, scope: NormalizationScope): boolean {
        // subtree paths are fine
        if (!this.isPathPrefix(scope.rootPath, path)) {
            return false;
        }

        // Local repairs inside the node currently being normalized are fine,
        // even if they happen before the surrounding resume cursor.
        if (scope.currentNodePath && this.isPathPrefix(scope.currentNodePath, path)) {
            return false;
        }

        // paths after the resume path are fine
        if (isPathEqual(path, scope.resumePath) || this.isPathPrefix(scope.resumePath, path)) {
            return false;
        }

        return isPathBefore(path, scope.resumePath) && !this.isPathPrefix(path, scope.resumePath);
    }

    /**
     * Returns true if the given position path is unsafe to modify within the normalization scope.
     *
     * Position-addressed writes like insertions differ from node-addressed writes: a prefix of the
     * resume path still means "insert before the current subtree", so it must be treated as unsafe
     * unless the write is inside the node currently being normalized.
     */
    private isUnsafePositionWithinNormalizationScope(
        path: Path,
        scope: NormalizationScope
    ): boolean {
        // subtree positions are fine
        if (!this.isPathPrefix(scope.rootPath, path)) {
            return false;
        }

        // Local repairs inside the node currently being normalized are fine.
        if (scope.currentNodePath && this.isPathPrefix(scope.currentNodePath, path)) {
            return false;
        }

        return isPathBefore(path, scope.resumePath) || isPathEqual(path, scope.resumePath);
    }

    /**
     * Normalize a `node` with a `schema`, but not its children.
     */
    private normalizeNode(node: KeyedNode, schema: Schema) {
        // We limit the number of iterations to prevent infinite loops.
        // Since a node can be normalized for each of its children, the limit depends on the number of children.
        const max =
            Math.max(node.object === 'text' ? 1 : node.nodes.size, 1) *
            (schema.validations.length + 1);
        let iterations = 0;

        const iterate = (n: KeyedNode) => {
            const normalize = schema.validateNode(n, this.value.document);
            if (!normalize) {
                return;
            }

            // Run the `normalize` function to fix the node.
            let path: Path | null = this.value.document.getPath(n.key);
            if (!path) {
                return;
            }

            const previousPaths = this.setCurrentNormalizationNodePath(path);
            try {
                normalize(this);
            } finally {
                this.restoreCurrentNormalizationNodePath(previousPaths);
            }

            // Re-find the node reference, in case it was updated. If the node no longer
            // exists, we're done for this branch.
            n = this.value.document.refindNode(path, n.key)!;
            if (!n) {
                return;
            }

            path = this.value.document.refindPath(path, n.key);

            // Increment the iterations counter, and check to make sure that we haven't
            // exceeded the max. Without this check, it's easy for the `validate` or
            // `normalize` function of a schema rule to be written incorrectly and for
            // an infinite invalid loop to occur.
            iterations += 1;

            if (iterations > max) {
                throw new Error(
                    `A schema rule could not be validated after sufficient iterations for ${
                        node.object
                    }#${
                        node.type || ''
                    }. This is usually due to a 'rule.validate' or 'rule.normalize' function of a schema being incorrectly written, causing an infinite loop.`
                );
            }

            // Otherwise, iterate again.
            iterate(n);
        };

        iterate(node);
    }

    // methods added by modifying the Change prototype

    declare public deleteBackward: (
        ...args: Tail<Parameters<Change['deleteBackwardAtRange']>>
    ) => Change;
    declare public deleteCharBackward: (
        ...args: Tail<Parameters<Change['deleteCharBackwardAtRange']>>
    ) => Change;
    declare public deleteLineBackward: (
        ...args: Tail<Parameters<Change['deleteLineBackwardAtRange']>>
    ) => Change;
    declare public deleteWordBackward: (
        ...args: Tail<Parameters<Change['deleteWordBackwardAtRange']>>
    ) => Change;
    declare public deleteForward: (
        ...args: Tail<Parameters<Change['deleteForwardAtRange']>>
    ) => Change;
    declare public deleteCharForward: (
        ...args: Tail<Parameters<Change['deleteCharForwardAtRange']>>
    ) => Change;
    declare public deleteWordForward: (
        ...args: Tail<Parameters<Change['deleteWordForwardAtRange']>>
    ) => Change;
    declare public deleteLineForward: (
        ...args: Tail<Parameters<Change['deleteLineForwardAtRange']>>
    ) => Change;
    declare public setBlocks: (...args: Tail<Parameters<Change['setBlocksAtRange']>>) => Change;
    declare public setInlines: (...args: Tail<Parameters<Change['setInlinesAtRange']>>) => Change;
    declare public splitInline: (...args: Tail<Parameters<Change['splitInlineAtRange']>>) => Change;
    declare public unwrapBlock: (...args: Tail<Parameters<Change['unwrapBlockAtRange']>>) => Change;
    declare public unwrapInline: (
        ...args: Tail<Parameters<Change['unwrapInlineAtRange']>>
    ) => Change;
    declare public wrapBlock: (...args: Tail<Parameters<Change['wrapBlockAtRange']>>) => Change;
    declare public wrapInline: (...args: Tail<Parameters<Change['wrapInlineAtRange']>>) => Change;

    declare public moveCharForward: () => Change;
    declare public moveStartCharForward: () => Change;
    declare public moveEndCharForward: () => Change;
    declare public extendCharForward: () => Change;
    declare public collapseCharForward: () => Change;
    declare public moveCharBackward: () => Change;
    declare public moveStartCharBackward: () => Change;
    declare public moveEndCharBackward: () => Change;
    declare public extendCharBackward: () => Change;
    declare public collapseCharBackward: () => Change;

    declare public collapseLineBackward: (
        ...args: Parameters<Change['collapseToStartOfBlock']>
    ) => Change;
    declare public collapseLineForward: (
        ...args: Parameters<Change['collapseToEndOfBlock']>
    ) => Change;
    declare public extendLineBackward: (
        ...args: Parameters<Change['extendToStartOfBlock']>
    ) => Change;
    declare public extendLineForward: (...args: Parameters<Change['extendToEndOfBlock']>) => Change;
    declare public blur: (...args: Parameters<TextRange['blur']>) => Change;
    declare public collapseTo: (...args: Parameters<TextRange['collapseTo']>) => Change;
    declare public collapseToAnchor: (...args: Parameters<TextRange['collapseToAnchor']>) => Change;
    declare public collapseToEnd: (...args: Parameters<TextRange['collapseToEnd']>) => Change;
    declare public collapseToEndOf: (...args: Parameters<TextRange['collapseToEndOf']>) => Change;
    declare public collapseToFocus: (...args: Parameters<TextRange['collapseToFocus']>) => Change;
    declare public collapseToStart: (...args: Parameters<TextRange['collapseToStart']>) => Change;
    declare public collapseToStartOf: (
        ...args: Parameters<TextRange['collapseToStartOf']>
    ) => Change;
    declare public extend: (...args: Parameters<TextRange['extend']>) => Change;
    declare public extendTo: (...args: Parameters<TextRange['extendTo']>) => Change;
    declare public extendToEndOf: (...args: Parameters<TextRange['extendToEndOf']>) => Change;
    declare public extendToStartOf: (...args: Parameters<TextRange['extendToStartOf']>) => Change;
    declare public flip: (...args: Parameters<TextRange['flip']>) => Change;
    declare public focus: (...args: Parameters<TextRange['focus']>) => Change;
    declare public move: (...args: Parameters<TextRange['move']>) => Change;
    declare public moveAnchor: (...args: Parameters<TextRange['moveAnchor']>) => Change;
    declare public moveAnchorOffsetTo: (
        ...args: Parameters<TextRange['moveAnchorOffsetTo']>
    ) => Change;
    declare public moveAnchorTo: (...args: Parameters<TextRange['moveAnchorTo']>) => Change;
    declare public moveAnchorToEndOf: (
        ...args: Parameters<TextRange['moveAnchorToEndOf']>
    ) => Change;
    declare public moveAnchorToStartOf: (
        ...args: Parameters<TextRange['moveAnchorToStartOf']>
    ) => Change;
    declare public moveEnd: (...args: Parameters<TextRange['moveEnd']>) => Change;
    declare public moveEndOffsetTo: (...args: Parameters<TextRange['moveEndOffsetTo']>) => Change;
    declare public moveEndTo: (...args: Parameters<TextRange['moveEndTo']>) => Change;
    declare public moveFocus: (...args: Parameters<TextRange['moveFocus']>) => Change;
    declare public moveFocusOffsetTo: (
        ...args: Parameters<TextRange['moveFocusOffsetTo']>
    ) => Change;
    declare public moveFocusTo: (...args: Parameters<TextRange['moveFocusTo']>) => Change;
    declare public moveFocusToEndOf: (...args: Parameters<TextRange['moveFocusToEndOf']>) => Change;
    declare public moveFocusToStartOf: (
        ...args: Parameters<TextRange['moveFocusToStartOf']>
    ) => Change;
    declare public moveOffsetsTo: (...args: Parameters<TextRange['moveOffsetsTo']>) => Change;
    declare public moveStart: (...args: Parameters<TextRange['moveStart']>) => Change;
    declare public moveStartOffsetTo: (
        ...args: Parameters<TextRange['moveStartOffsetTo']>
    ) => Change;
    declare public moveStartTo: (...args: Parameters<TextRange['moveStartTo']>) => Change;
    declare public moveTo: (...args: Parameters<TextRange['moveTo']>) => Change;
    declare public moveToEnd: (...args: Parameters<TextRange['moveToEnd']>) => Change;
    declare public moveToEndOf: (...args: Parameters<TextRange['moveToEndOf']>) => Change;
    declare public moveToRangeOf: (...args: Parameters<TextRange['moveToRangeOf']>) => Change;
    declare public moveToStart: (...args: Parameters<TextRange['moveToStart']>) => Change;
    declare public moveToStartOf: (...args: Parameters<TextRange['moveToStartOf']>) => Change;
    declare public deselect: (...args: Parameters<TextRange['deselect']>) => Change;

    // start - block
    declare public moveToStartOfBlock: () => Change;
    declare public moveAnchorToStartOfBlock: () => Change;
    declare public moveFocusToStartOfBlock: () => Change;
    declare public moveStartToStartOfBlock: () => Change;
    declare public moveEndToStartOfBlock: () => Change;
    declare public collapseToStartOfBlock: () => Change;
    declare public extendToStartOfBlock: () => Change;
    // start - inline
    declare public moveToStartOfInline: () => Change;
    declare public moveAnchorToStartOfInline: () => Change;
    declare public moveFocusToStartOfInline: () => Change;
    declare public moveStartToStartOfInline: () => Change;
    declare public moveEndToStartOfInline: () => Change;
    declare public collapseToStartOfInline: () => Change;
    declare public extendToStartOfInline: () => Change;
    // start - text
    declare public moveToStartOfText: () => Change;
    declare public moveAnchorToStartOfText: () => Change;
    declare public moveFocusToStartOfText: () => Change;
    declare public moveStartToStartOfText: () => Change;
    declare public moveEndToStartOfText: () => Change;
    declare public collapseToStartOfText: () => Change;
    declare public extendToStartOfText: () => Change;
    // start - next - block
    declare public moveToStartOfNextBlock: () => Change;
    declare public moveAnchorToStartOfNextBlock: () => Change;
    declare public moveFocusToStartOfNextBlock: () => Change;
    declare public moveStartToStartOfNextBlock: () => Change;
    declare public moveEndToStartOfNextBlock: () => Change;
    declare public collapseToStartOfNextBlock: () => Change;
    declare public extendToStartOfNextBlock: () => Change;
    // start - next - inline
    declare public moveToStartOfNextInline: () => Change;
    declare public moveAnchorToStartOfNextInline: () => Change;
    declare public moveFocusToStartOfNextInline: () => Change;
    declare public moveStartToStartOfNextInline: () => Change;
    declare public moveEndToStartOfNextInline: () => Change;
    declare public collapseToStartOfNextInline: () => Change;
    declare public extendToStartOfNextInline: () => Change;
    // start - next - text
    declare public moveToStartOfNextText: () => Change;
    declare public moveAnchorToStartOfNextText: () => Change;
    declare public moveFocusToStartOfNextText: () => Change;
    declare public moveStartToStartOfNextText: () => Change;
    declare public moveEndToStartOfNextText: () => Change;
    declare public collapseToStartOfNextText: () => Change;
    declare public extendToStartOfNextText: () => Change;
    // start - previous - block
    declare public moveToStartOfPreviousBlock: () => Change;
    declare public moveAnchorToStartOfPreviousBlock: () => Change;
    declare public moveFocusToStartOfPreviousBlock: () => Change;
    declare public moveStartToStartOfPreviousBlock: () => Change;
    declare public moveEndToStartOfPreviousBlock: () => Change;
    declare public collapseToStartOfPreviousBlock: () => Change;
    declare public extendToStartOfPreviousBlock: () => Change;
    // start - previous - inline
    declare public moveToStartOfPreviousInline: () => Change;
    declare public moveAnchorToStartOfPreviousInline: () => Change;
    declare public moveFocusToStartOfPreviousInline: () => Change;
    declare public moveStartToStartOfPreviousInline: () => Change;
    declare public moveEndToStartOfPreviousInline: () => Change;
    declare public collapseToStartOfPreviousInline: () => Change;
    declare public extendToStartOfPreviousInline: () => Change;
    // start - previous - text
    declare public moveToStartOfPreviousText: () => Change;
    declare public moveAnchorToStartOfPreviousText: () => Change;
    declare public moveFocusToStartOfPreviousText: () => Change;
    declare public moveStartToStartOfPreviousText: () => Change;
    declare public moveEndToStartOfPreviousText: () => Change;
    declare public collapseToStartOfPreviousText: () => Change;
    declare public extendToStartOfPreviousText: () => Change;
    // end - block
    declare public moveToEndOfBlock: () => Change;
    declare public moveAnchorToEndOfBlock: () => Change;
    declare public moveFocusToEndOfBlock: () => Change;
    declare public moveStartToEndOfBlock: () => Change;
    declare public moveEndToEndOfBlock: () => Change;
    declare public collapseToEndOfBlock: () => Change;
    declare public extendToEndOfBlock: () => Change;
    // end - inline
    declare public moveToEndOfInline: () => Change;
    declare public moveAnchorToEndOfInline: () => Change;
    declare public moveFocusToEndOfInline: () => Change;
    declare public moveStartToEndOfInline: () => Change;
    declare public moveEndToEndOfInline: () => Change;
    declare public collapseToEndOfInline: () => Change;
    declare public extendToEndOfInline: () => Change;
    // end - text
    declare public moveToEndOfText: () => Change;
    declare public moveAnchorToEndOfText: () => Change;
    declare public moveFocusToEndOfText: () => Change;
    declare public moveStartToEndOfText: () => Change;
    declare public moveEndToEndOfText: () => Change;
    declare public collapseToEndOfText: () => Change;
    declare public extendToEndOfText: () => Change;
    // end - next - block
    declare public moveToEndOfNextBlock: () => Change;
    declare public moveAnchorToEndOfNextBlock: () => Change;
    declare public moveFocusToEndOfNextBlock: () => Change;
    declare public moveStartToEndOfNextBlock: () => Change;
    declare public moveEndToEndOfNextBlock: () => Change;
    declare public collapseToEndOfNextBlock: () => Change;
    declare public extendToEndOfNextBlock: () => Change;
    // end - next - inline
    declare public moveToEndOfNextInline: () => Change;
    declare public moveAnchorToEndOfNextInline: () => Change;
    declare public moveFocusToEndOfNextInline: () => Change;
    declare public moveStartToEndOfNextInline: () => Change;
    declare public moveEndToEndOfNextInline: () => Change;
    declare public collapseToEndOfNextInline: () => Change;
    declare public extendToEndOfNextInline: () => Change;
    // end - next - text
    declare public moveToEndOfNextText: () => Change;
    declare public moveAnchorToEndOfNextText: () => Change;
    declare public moveFocusToEndOfNextText: () => Change;
    declare public moveStartToEndOfNextText: () => Change;
    declare public moveEndToEndOfNextText: () => Change;
    declare public collapseToEndOfNextText: () => Change;
    declare public extendToEndOfNextText: () => Change;
    // end - previous - block
    declare public moveToEndOfPreviousBlock: () => Change;
    declare public moveAnchorToEndOfPreviousBlock: () => Change;
    declare public moveFocusToEndOfPreviousBlock: () => Change;
    declare public moveStartToEndOfPreviousBlock: () => Change;
    declare public moveEndToEndOfPreviousBlock: () => Change;
    declare public collapseToEndOfPreviousBlock: () => Change;
    declare public extendToEndOfPreviousBlock: () => Change;
    // end - previous - inline
    declare public moveToEndOfPreviousInline: () => Change;
    declare public moveAnchorToEndOfPreviousInline: () => Change;
    declare public moveFocusToEndOfPreviousInline: () => Change;
    declare public moveStartToEndOfPreviousInline: () => Change;
    declare public moveEndToEndOfPreviousInline: () => Change;
    declare public collapseToEndOfPreviousInline: () => Change;
    declare public extendToEndOfPreviousInline: () => Change;
    // end - previous - text
    declare public moveToEndOfPreviousText: () => Change;
    declare public moveAnchorToEndOfPreviousText: () => Change;
    declare public moveFocusToEndOfPreviousText: () => Change;
    declare public moveStartToEndOfPreviousText: () => Change;
    declare public moveEndToEndOfPreviousText: () => Change;
    declare public collapseToEndOfPreviousText: () => Change;
    declare public extendToEndOfPreviousText: () => Change;
    // range - block
    declare public moveToRangeOfBlock: () => Change;
    // range - inline
    declare public moveToRangeOfInline: () => Change;
    // range - text
    declare public moveToRangeOfText: () => Change;
    // range - next - block
    declare public moveToRangeOfNextBlock: () => Change;
    // range - next - inline
    declare public moveToRangeOfNextInline: () => Change;
    // range - next - text
    declare public moveToRangeOfNextText: () => Change;
    // range - previous - block
    declare public moveToRangeOfPreviousBlock: () => Change;
    // range - previous - inline
    declare public moveToRangeOfPreviousInline: () => Change;
    // range - previous - text
    declare public moveToRangeOfPreviousText: () => Change;
}

/*
 * Mix in the changes that pass through to their at-range equivalents because
 * they don't have any effect on the selection.
 */
[
    'deleteBackward',
    'deleteCharBackward',
    'deleteLineBackward',
    'deleteWordBackward',
    'deleteForward',
    'deleteCharForward',
    'deleteWordForward',
    'deleteLineForward',
    'setBlocks',
    'setInlines',
    'splitInline',
    'unwrapBlock',
    'unwrapInline',
    'wrapBlock',
    'wrapInline',
].forEach((method) => {
    Change.prototype[method] = function (...args) {
        const { value } = this;
        const { selection } = value;
        const methodAtRange = `${method}AtRange`;
        this[methodAtRange](selection, ...args);

        if (method.match(/Backward$/)) {
            this.collapseToStart();
        } else if (method.match(/Forward$/)) {
            this.collapseToEnd();
        }
        return this;
    };
});

/*
 * Mix in move methods.
 */

['Forward', 'Backward'].forEach((direction) => {
    const anchor = `moveAnchorChar${direction}`;
    const focus = `moveFocusChar${direction}`;

    Change.prototype[`moveChar${direction}`] = function () {
        this[anchor]();
        this[focus]();
        return this;
    };

    Change.prototype[`moveStartChar${direction}`] = function () {
        if (this.value.isBackward) {
            this[focus]();
        } else {
            this[anchor]();
        }
        return this;
    };

    Change.prototype[`moveEndChar${direction}`] = function () {
        if (this.value.isBackward) {
            this[anchor]();
        } else {
            this[focus]();
        }
        return this;
    };

    Change.prototype[`extendChar${direction}`] = function () {
        this[`moveFocusChar${direction}`]();
        return this;
    };

    Change.prototype[`collapseChar${direction}`] = function () {
        const collapse = direction === 'Forward' ? 'collapseToEnd' : 'collapseToStart';
        this[collapse]();
        this[`moveChar${direction}`]();
        return this;
    };
});

/*
 * Mix in alias methods.
 */
[
    ['collapseLineBackward', 'collapseToStartOfBlock'],
    ['collapseLineForward', 'collapseToEndOfBlock'],
    ['extendLineBackward', 'extendToStartOfBlock'],
    ['extendLineForward', 'extendToEndOfBlock'],
].forEach(([alias, method]) => {
    Change.prototype[alias] = function (...args) {
        this[method](...args);
        return this;
    };
});

/*
 * Mix in selection changes that are just a proxy for the selection method.
 */
[
    'blur',
    'collapseTo',
    'collapseToAnchor',
    'collapseToEnd',
    'collapseToEndOf',
    'collapseToFocus',
    'collapseToStart',
    'collapseToStartOf',
    'extend',
    'extendTo',
    'extendToEndOf',
    'extendToStartOf',
    'flip',
    'focus',
    'move',
    'moveAnchor',
    'moveAnchorOffsetTo',
    'moveAnchorTo',
    'moveAnchorToEndOf',
    'moveAnchorToStartOf',
    'moveEnd',
    'moveEndOffsetTo',
    'moveEndTo',
    'moveFocus',
    'moveFocusOffsetTo',
    'moveFocusTo',
    'moveFocusToEndOf',
    'moveFocusToStartOf',
    'moveOffsetsTo',
    'moveStart',
    'moveStartOffsetTo',
    'moveStartTo',
    'moveTo',
    'moveToEnd',
    'moveToEndOf',
    'moveToRangeOf',
    'moveToStart',
    'moveToStartOf',
    'deselect',
].forEach((method) => {
    Change.prototype[method] = function (...args) {
        const normalize = method !== 'deselect';
        const { value } = this as Change;
        const { document, selection } = value;
        const fn = selection[method];

        if (typeof fn !== 'function') {
            if (isBlockRangeSelection(selection)) {
                // Convert the selection to a text range and try again.
                const range = TextRange.fromBlockRange(selection, document);
                return this.select(range)[method](...args);
            }
            throw new Error(`The method \`${method}\` has not been implemented by TextRange.`);
        }

        let next = fn.call(selection, ...args);
        if (normalize) {
            next = next.normalize(document);
        }
        this.select(next);

        return this;
    };
});

/*
 * Mix in node-related changes.
 */
[
    'moveTo',
    'moveAnchorTo',
    'moveFocusTo',
    'moveStartTo',
    'moveEndTo',
    'collapseTo',
    'extendTo',
].forEach((prefix) => {
    const edges = ['Start', 'End'];
    const DIRECTIONS = ['Next', 'Previous'];
    const OBJECTS = ['Block', 'Inline', 'Text'];

    if (prefix === 'moveTo') {
        edges.push('TextRange');
    }

    edges.forEach((edge) => {
        const method = `${prefix}${edge}Of`;

        OBJECTS.forEach((object) => {
            const getNode = object === 'Text' ? 'getNode' : `getClosest${object}`;

            Change.prototype[`${method}${object}`] = function () {
                const { value } = this;
                const { document, selection } = value;
                const node = document[getNode](selection.startKey);
                if (!node) {
                    return this;
                }
                this[method](node);

                return this;
            };

            DIRECTIONS.forEach((direction) => {
                const getDirectionNode = `get${direction}${object}`;
                const directionKey = direction === 'Next' ? 'startKey' : 'endKey';

                Change.prototype[`${method}${direction}${object}`] = function () {
                    const { value } = this;
                    const { document, selection } = value;
                    const node = document[getNode](selection[directionKey]);
                    if (!node) {
                        return this;
                    }
                    const target = document[getDirectionNode](node.key);
                    if (!target) {
                        return this;
                    }
                    this[method](target);

                    return this;
                };
            });
        });
    });
});

/*
 * Attach a pseudo-symbol for type checking.
 */

Change.prototype[MODEL_TYPES.CHANGE] = true;
